import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShopifyPayouts } from "@/components/financiar/ShopifyPayouts";
import { Wallet, TrendingUp, TrendingDown, Receipt, Plus, Trash2, Save, ChevronLeft, ChevronRight, X, Truck, Package, RefreshCw, Send, Calendar, Pencil, Upload, FileDown, CreditCard, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/financiar`;

const fmt = (n, currency = "RON") => {
  const v = Number(n || 0);
  return `${v.toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
};

const MONTHS_RO = [
  "Ianuarie", "Februarie", "Martie", "Aprilie", "Mai", "Iunie",
  "Iulie", "August", "Septembrie", "Octombrie", "Noiembrie", "Decembrie",
];

// ─── Add / Edit Bill Modal ──────────────────────────────────────────
// Same modal, dual purpose: `editing` (a factura object) → PUT, else POST.
// New fields (2026-02-04): Nr Factură, Currency (RON/EUR/USD), Payment Status.
// When currency ≠ RON → TVA is auto-locked to 0.
function AddBillModal({ open, onClose, onCreated, vatRate, editing, country = "RO" }) {
  const [nume, setNume] = useState("");
  const [numarFactura, setNumarFactura] = useState("");
  const [valFara, setValFara] = useState("");
  const [tva, setTva] = useState("");
  const [total, setTotal] = useState("");
  const [data, setData] = useState(() => new Date().toISOString().slice(0, 10));
  const [dataScadenta, setDataScadenta] = useState("");
  const localCur = country === "BG" ? "EUR" : "RON";
  const [currency, setCurrency] = useState(localCur);
  const [paymentStatus, setPaymentStatus] = useState("unpaid");
  const [paidAmount, setPaidAmount] = useState("");
  const [detalii, setDetalii] = useState("");
  const [saving, setSaving] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [showSuppliers, setShowSuppliers] = useState(false);

  // Load supplier autocomplete list every time the modal opens.
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/suppliers`, { params: { country } });
        setSuppliers(Array.isArray(r.data?.items) ? r.data.items : []);
      } catch (_e) { /* autocomplete is optional */ }
    })();
  }, [open, country]);

  // Filter suggestions live as user types. Case-insensitive substring match,
  // top 8 results. Hide dropdown when user picks or clears.
  const supplierSuggestions = useMemo(() => {
    const q = (nume || "").trim().toLowerCase();
    if (!q) return suppliers.slice(0, 8);
    return suppliers
      .filter((s) => (s.nume || "").toLowerCase().includes(q))
      .slice(0, 8);
  }, [nume, suppliers]);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setNume(editing.nume || "");
      setNumarFactura(editing.numar_factura || "");
      setValFara(String(editing.valoare_fara_tva ?? ""));
      setTva(String(editing.tva ?? ""));
      setTotal(String(editing.valoare_totala ?? ""));
      setData(editing.data || new Date().toISOString().slice(0, 10));
      setDataScadenta(editing.data_scadenta || "");
      setCurrency(editing.currency || localCur);
      setPaymentStatus(editing.payment_status || "unpaid");
      setPaidAmount(String(editing.paid_amount ?? ""));
      setDetalii(editing.detalii || "");
    } else {
      setNume(""); setNumarFactura(""); setValFara(""); setTva(""); setTotal("");
      setData(new Date().toISOString().slice(0, 10));
      setDataScadenta("");
      setCurrency(localCur); setPaymentStatus("unpaid"); setPaidAmount("");
      setDetalii("");
    }
  }, [open, editing]);

  // Effective VAT rate — 0 for non-RON currencies (per user rule).
  const effectiveVatRate = currency === localCur ? vatRate : 0;

  const onCurrencyChange = (c) => {
    setCurrency(c);
    if (c !== localCur) {
      setTva("0");
      const f = parseFloat(valFara);
      if (Number.isFinite(f)) setTotal(f.toFixed(2));
    } else {
      // Restore default TVA on switch back to RON.
      const f = parseFloat(valFara);
      if (Number.isFinite(f)) {
        const t = f * vatRate;
        setTva(t.toFixed(2));
        setTotal((f + t).toFixed(2));
      }
    }
  };

  const onValFaraChange = (v) => {
    setValFara(v);
    const n = parseFloat(v);
    if (Number.isFinite(n)) {
      const t = n * effectiveVatRate;
      setTva(t.toFixed(2));
      setTotal((n + t).toFixed(2));
    }
  };
  const onTvaChange = (v) => {
    if (currency !== localCur) return; // Locked at 0
    setTva(v);
    const f = parseFloat(valFara), t = parseFloat(v);
    if (Number.isFinite(f) && Number.isFinite(t)) setTotal((f + t).toFixed(2));
  };
  const onTotalChange = (v) => {
    setTotal(v);
    const f = parseFloat(valFara), tt = parseFloat(v);
    if (currency === localCur && Number.isFinite(f) && Number.isFinite(tt)) setTva((tt - f).toFixed(2));
  };

  const totalNum = parseFloat(total) || 0;
  const paidNum = parseFloat(paidAmount) || 0;
  const outstanding = Math.max(0, totalNum - paidNum);
  const currencySym = ({USD:"$", EUR:"€", HUF:"Ft", BGN:"лв"})[currency] || currency;

  const submit = async () => {
    if (!nume.trim()) { toast.error("Numele facturii este obligatoriu"); return; }
    const f = parseFloat(valFara), t = parseFloat(tva), tt = parseFloat(total);
    if (!Number.isFinite(f) || !Number.isFinite(t) || !Number.isFinite(tt)) {
      toast.error("Completează toate valorile numerice"); return;
    }
    if (paymentStatus === "partial") {
      if (!Number.isFinite(paidNum) || paidNum <= 0) {
        toast.error("Suma achitată parțial trebuie să fie > 0"); return;
      }
      if (paidNum >= tt) {
        toast.error(`Suma achitată (${paidNum}) e >= total (${tt}). Alege status „Plătită".`); return;
      }
    }
    setSaving(true);
    try {
      const body = {
        country: "RO",
        nume: nume.trim(),
        numar_factura: numarFactura.trim() || null,
        valoare_fara_tva: f, tva: t, valoare_totala: tt, data,
        data_scadenta: dataScadenta || null,
        currency,
        payment_status: paymentStatus,
        paid_amount: paymentStatus === "partial" ? paidNum : (paymentStatus === "paid" ? tt : 0),
        detalii: detalii.trim() || null,
      };
      const r = editing
        ? await axios.put(`${API}/facturi/${editing.id}`, body)
        : await axios.post(`${API}/facturi`, body);
      toast.success(editing ? "Factură actualizată" : "Factură adăugată");
      onCreated?.(r.data);
      onClose();
    } catch (e) {
      // Backend returns HTTP 409 with a descriptive `detail` when the
      // invoice (country + numar_factura + valoare_totala) is already
      // recorded. Surface it clearly instead of the generic „Eroare".
      if (e.response?.status === 409) {
        toast.error("Factură duplicat", {
          description: e.response?.data?.detail || "Factura există deja.",
          duration: 12000,
        });
      } else {
        toast.error("Eroare la salvare: " + (e.response?.data?.detail || e.message));
      }
    } finally { setSaving(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="add-bill-modal">
      <div className="w-full max-w-lg rounded-lg border border-zinc-800 bg-zinc-950 p-6 shadow-2xl">
        <div className="flex items-center justify-between border-b border-zinc-800 pb-3">
          <h3 className="text-lg font-semibold text-zinc-100">{editing ? "Editează factură" : "Introdu factură nouă"}</h3>
          <button onClick={onClose} className="rounded p-1 text-zinc-400 hover:bg-zinc-800" data-testid="add-bill-close">
            <X size={18} />
          </button>
        </div>
        <div className="mt-4 space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-zinc-400">Nr Factură</label>
              <input value={numarFactura} onChange={(e) => setNumarFactura(e.target.value)} data-testid="add-bill-invoice-nr"
                placeholder="Ex: F-2026-001"
                className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 focus:border-zinc-600 focus:outline-none" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-zinc-400">Data emitere</label>
              <input type="date" value={data} onChange={(e) => setData(e.target.value)} data-testid="add-bill-date"
                className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 focus:border-zinc-600 focus:outline-none" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-zinc-400">Data scadență</label>
              <input type="date" value={dataScadenta} onChange={(e) => setDataScadenta(e.target.value)} data-testid="add-bill-due-date"
                min={data}
                className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 focus:border-zinc-600 focus:outline-none" />
            </div>
          </div>
          <div className="relative">
            <label className="mb-1 block text-xs font-medium text-zinc-400">Nume furnizor / descriere</label>
            <input value={nume}
              onChange={(e) => { setNume(e.target.value); setShowSuppliers(true); }}
              onFocus={() => setShowSuppliers(true)}
              onBlur={() => setTimeout(() => setShowSuppliers(false), 150)}
              data-testid="add-bill-name"
              placeholder="Ex: Import Alibaba, Chirie, Comision Oblio..."
              autoComplete="off"
              className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 focus:border-zinc-600 focus:outline-none" />
            {showSuppliers && supplierSuggestions.length > 0 && (
              <div
                data-testid="add-bill-name-suggestions"
                className="absolute left-0 right-0 top-full z-20 mt-1 max-h-56 overflow-y-auto rounded-md border border-zinc-800 bg-zinc-950 shadow-xl"
              >
                {supplierSuggestions.map((s) => (
                  <button
                    key={s.nume}
                    type="button"
                    data-testid={`add-bill-suggestion-${s.nume}`}
                    onMouseDown={(e) => { e.preventDefault(); setNume(s.nume); setShowSuppliers(false); }}
                    className="flex w-full items-center justify-between border-b border-zinc-900 px-3 py-2 text-left transition last:border-b-0 hover:bg-zinc-900/60"
                  >
                    <span className="truncate text-xs text-zinc-100">{s.nume}</span>
                    <span className="ml-2 shrink-0 text-[10px] text-zinc-500">{s.count} fact.</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-zinc-400">Monedă</label>
            <div className="flex gap-2">
              {["RON", "EUR", "USD", "HUF", "BGN"].map((c) => (
                <button key={c} type="button" onClick={() => onCurrencyChange(c)}
                  data-testid={`add-bill-currency-${c}`}
                  className={`flex-1 rounded-md border px-3 py-1.5 text-sm font-medium transition ${currency === c ? "border-orange-500 bg-orange-500/20 text-orange-100" : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:border-zinc-700"}`}>
                  {c}
                </button>
              ))}
            </div>
            {currency !== localCur && (
              <div className="mt-1 text-[11px] text-amber-400/80">TVA-ul este 0 automat pentru {currency} (import/servicii extern).</div>
            )}
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-zinc-400">Val. fără TVA ({currencySym})</label>
              <input type="number" step="0.01" value={valFara} onChange={(e) => onValFaraChange(e.target.value)} data-testid="add-bill-net"
                className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 focus:border-zinc-600 focus:outline-none" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-zinc-400">TVA ({(effectiveVatRate * 100).toFixed(0)}%)</label>
              <input type="number" step="0.01" value={tva} onChange={(e) => onTvaChange(e.target.value)}
                disabled={currency !== localCur}
                data-testid="add-bill-vat"
                className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 focus:border-zinc-600 focus:outline-none disabled:opacity-50" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-zinc-400">Total ({currencySym})</label>
              <input type="number" step="0.01" value={total} onChange={(e) => onTotalChange(e.target.value)} data-testid="add-bill-total"
                className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 focus:border-zinc-600 focus:outline-none" />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-zinc-400">Status plată</label>
            <div className="flex gap-2">
              {[
                { k: "unpaid", label: "Neplătită", cls: "border-rose-500 bg-rose-500/20 text-rose-100" },
                { k: "partial", label: "Achitată parțial", cls: "border-amber-500 bg-amber-500/20 text-amber-100" },
                { k: "paid", label: "Plătită", cls: "border-emerald-500 bg-emerald-500/20 text-emerald-100" },
              ].map((s) => (
                <button key={s.k} type="button" onClick={() => setPaymentStatus(s.k)}
                  data-testid={`add-bill-status-${s.k}`}
                  className={`flex-1 rounded-md border px-3 py-1.5 text-sm font-medium transition ${paymentStatus === s.k ? s.cls : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:border-zinc-700"}`}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          {paymentStatus === "partial" && (
            <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3">
              <label className="mb-1 block text-xs font-medium text-amber-200">Sumă achitată deja ({currencySym})</label>
              <input type="number" step="0.01" value={paidAmount}
                onChange={(e) => setPaidAmount(e.target.value)}
                data-testid="add-bill-partial-amount"
                placeholder="Ex: 500.00"
                className="w-full rounded-md border border-amber-500/40 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 focus:border-amber-500 focus:outline-none" />
              <div className="mt-2 text-xs text-amber-100">
                Diferență rămasă: <span className="font-bold">{outstanding.toFixed(2)} {currencySym}</span>
              </div>
            </div>
          )}
          <div>
            <label className="mb-1 block text-xs font-medium text-zinc-400">
              Detalii factură <span className="text-zinc-600">(opțional)</span>
            </label>
            <textarea
              value={detalii}
              onChange={(e) => setDetalii(e.target.value)}
              data-testid="add-bill-details"
              placeholder="Ex: Comision curier lunar, Chirie birou august, Materiale ambalare 500 buc..."
              rows={3}
              maxLength={2000}
              className="w-full resize-y rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 focus:border-zinc-600 focus:outline-none"
            />
            <div className="mt-0.5 text-right text-[10px] text-zinc-500">{detalii.length}/2000</div>
          </div>
        </div>
        <div className="mt-5 flex items-center justify-end gap-2 border-t border-zinc-800 pt-4">
          <button onClick={onClose} className="rounded-md border border-zinc-800 bg-zinc-900 px-4 py-2 text-sm text-zinc-300 hover:bg-zinc-800">
            Anulează
          </button>
          <button onClick={submit} disabled={saving} data-testid="add-bill-submit"
            className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-500 disabled:opacity-50">
            {saving ? "Se salvează..." : (editing ? "Salvează modificările" : "Salvează factură")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Bulk PDF import (Gemini) ─────────────────────────────────────
// Reads facturi PDF-uri, extrage nr/data/furnizor/total/monedă, inserează
// direct ca facturi PLĂTITE. Popup rezumat cu editare/ștergere.
function BulkPdfImport({ country, onImported }) {
  const [busy, setBusy] = useState(false);
  const [summaryModal, setSummaryModal] = useState(null); // {results, imported, failed}
  const [dragOver, setDragOver] = useState(false);
  const inputRef = React.useRef(null);

  const upload = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.name?.toLowerCase().endsWith(".pdf"));
    if (!files.length) { toast.error("Selectează cel puțin un PDF."); return; }
    if (files.length > 40) { toast.error("Maxim 40 PDF-uri per import."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const r = await axios.post(`${API}/facturi/bulk-import-pdf`, fd, {
        params: { country },
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000, // 5 min for 40 large PDFs
      });
      setSummaryModal(r.data);
      // Toast dedicat pentru fisierele duplicat detectate (user request Feb 2026):
      // motiv clar in popup, nu doar in summary card.
      const dupResults = (r.data?.results || []).filter(
        (rr) => !rr.ok && (String(rr.error || "").toLowerCase().includes("duplicat"))
      );
      dupResults.forEach((dr) => {
        toast.error("Factură duplicat", {
          description: `${dr.filename}: ${dr.error}`,
          duration: 10000,
        });
      });
      if (r.data?.imported > 0) {
        toast.success(`${r.data.imported}/${r.data.total_files} facturi importate în ${country}${dupResults.length ? ` · ${dupResults.length} duplicate ignorate` : ""}`);
        onImported && onImported();
      } else if (dupResults.length === 0) {
        toast.error(`0 facturi importate — verifică erorile în popup.`);
      }
    } catch (e) {
      toast.error("Import eșuat: " + (e?.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    upload(e.dataTransfer?.files);
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="application/pdf"
          className="hidden"
          data-testid={`bulk-pdf-import-input-${country}`}
          onChange={(e) => { upload(e.target.files); e.target.value = ""; }}
        />
        <button
          data-testid={`bulk-pdf-import-btn-${country}`}
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-md border border-cyan-500/40 bg-cyan-500/10 px-3 py-2 text-sm font-medium text-cyan-200 transition hover:bg-cyan-500/20 disabled:opacity-50"
        >
          <Upload size={14} /> {busy ? "Se importă (Gemini)..." : "Import bulk PDF"}
        </button>
        <div
          data-testid={`bulk-pdf-import-dropzone-${country}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`hidden flex-1 items-center justify-center rounded-md border border-dashed px-3 py-2 text-[11px] transition sm:inline-flex ${
            dragOver ? "border-cyan-500 bg-cyan-500/10 text-cyan-200" : "border-zinc-800 bg-zinc-950 text-zinc-500"
          }`}
        >
          sau trage PDF-urile aici · max 40 · doar citire, nu se salvează fișierul
        </div>
      </div>
      <ImportSummaryModal open={!!summaryModal} data={summaryModal} onClose={() => setSummaryModal(null)} onRefresh={onImported} />
    </>
  );
}

// ─── Bulk XLS import (eMAG marketplace finance export) ─────────────
// User 2026-02-04: „butonul de importa xls doar afiseaza facturile in
// Financiar TVA". No PDF is attached — pure data import from the eMAG XLS
// (columns E/H/J/L/M/N/O/P/S/T). Same dedup logic as bulk-import-pdf.
function BulkXlsImport({ country, onImported }) {
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(null); // {imported, skipped_duplicates, skipped_invalid, results}
  const inputRef = React.useRef(null);

  const upload = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => /\.xlsx?$/i.test(f.name || ""));
    if (!files.length) { toast.error("Selectează un fișier .xls sau .xlsx."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", files[0]);
      const r = await axios.post(`${API}/facturi/bulk-import-xls`, fd, {
        params: { country },
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      setSummary(r.data);
      // Toast dedicat pentru duplicate din XLS (user request Feb 2026).
      const dupResults = (r.data?.results || []).filter(
        (rr) => !rr.ok && (String(rr.error || "").toLowerCase().includes("duplicat"))
      );
      dupResults.slice(0, 5).forEach((dr) => {
        toast.error("Factură duplicat (XLS)", {
          description: `${dr.numar_factura || dr.filename || "rând"}: ${dr.error}`,
          duration: 10000,
        });
      });
      if (dupResults.length > 5) {
        toast.error(`+${dupResults.length - 5} facturi duplicat suplimentare`, {
          description: "Vezi lista completă în popup-ul de mai jos.",
          duration: 8000,
        });
      }
      if (r.data?.imported > 0) {
        toast.success(`${r.data.imported} facturi importate din XLS (${r.data.skipped_duplicates} duplicate)`);
        onImported && onImported();
      } else if (dupResults.length === 0) {
        toast.error(`0 facturi importate — vezi popup (dup: ${r.data?.skipped_duplicates || 0}, invalid: ${r.data?.skipped_invalid || 0}).`);
      }
    } catch (e) {
      toast.error("Import XLS eșuat: " + (e?.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        className="hidden"
        data-testid={`bulk-xls-import-input-${country}`}
        onChange={(e) => { upload(e.target.files); e.target.value = ""; }}
      />
      <button
        data-testid={`bulk-xls-import-btn-${country}`}
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        className="inline-flex items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm font-medium text-amber-200 transition hover:bg-amber-500/20 disabled:opacity-50"
        title="Import facturi eMAG dintr-un fișier XLS/XLSX descărcat din contul de marketplace"
      >
        <Upload size={14} /> {busy ? "Se importă..." : "Import XLS eMAG"}
      </button>
      {summary && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setSummary(null)}>
          <div className="w-full max-w-2xl max-h-[85vh] overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-950 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-zinc-800 px-6 py-4">
              <div>
                <h3 className="text-lg font-semibold text-zinc-100">Import XLS eMAG · rezultat</h3>
                <p className="mt-0.5 text-[11px] text-zinc-500">
                  Fișier: <span className="font-mono text-zinc-300">{summary.filename}</span> · Țară: {summary.country}
                </p>
              </div>
              <button data-testid="xls-import-close" onClick={() => setSummary(null)}
                className="rounded-md p-2 text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200">✕</button>
            </div>
            <div className="grid grid-cols-4 gap-3 border-b border-zinc-800 p-4 text-center">
              <div>
                <div className="text-[10px] uppercase text-zinc-500">Rânduri</div>
                <div className="mt-1 text-xl font-semibold text-zinc-100">{summary.total_rows}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-zinc-500">Importate</div>
                <div className="mt-1 text-xl font-semibold text-emerald-300">{summary.imported}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-zinc-500">Duplicate</div>
                <div className="mt-1 text-xl font-semibold text-amber-300">{summary.skipped_duplicates}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-zinc-500">Invalide</div>
                <div className="mt-1 text-xl font-semibold text-rose-300">{summary.skipped_invalid}</div>
              </div>
            </div>
            <div className="max-h-[50vh] overflow-y-auto px-4 py-3">
              {(summary.results || []).slice(0, 200).map((r, i) => (
                <div key={i} className={`mb-1 rounded border px-2 py-1 text-[11px] ${
                  r.ok ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-200"
                       : "border-rose-500/30 bg-rose-500/5 text-rose-200"}`}>
                  <span className="font-mono text-[10px] text-zinc-500 mr-2">rând {r.row}</span>
                  {r.ok
                    ? <>✓ {r.invoice.nume} · {r.invoice.numar_factura} · {r.invoice.valoare_totala} {r.invoice.currency} · {r.invoice.data}</>
                    : <>✕ {r.error}</>}
                </div>
              ))}
              {(summary.results || []).length > 200 && (
                <div className="mt-2 text-center text-[10px] text-zinc-500">
                  ... și încă {summary.results.length - 200} rânduri (nu sunt afișate).
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Import summary popup ────────────────────────────────────────
// Arată rezultatele extracției pe fiecare PDF, cu opțiuni de editare a
// numelui furnizor / total / dată și de ștergere directă a facturii dacă
// datele extrase nu sunt corecte (user testează cu 2-3 facturi înainte
// de import mare).
export function ImportSummaryModal({ open, data, onClose, onRefresh }) {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (!open || !data) { setRows([]); return; }
    setRows((data.results || []).map((r, idx) => ({ ...r, _key: idx })));
  }, [open, data]);

  if (!open || !data) return null;

  const deleteRow = async (row) => {
    const inv = row.invoice;
    if (!inv?.id) return;
    if (!window.confirm(`Șterg factura ${inv.numar_factura || inv.id} de la ${inv.nume}?`)) return;
    try {
      await axios.delete(`${API}/facturi/${inv.id}`);
      setRows((prev) => prev.filter((r) => r._key !== row._key));
      toast.success("Factură ștearsă");
      onRefresh && onRefresh();
    } catch (e) {
      toast.error("Ștergere eșuată: " + (e?.response?.data?.detail || e.message));
    }
  };

  const editRow = async (row, patch) => {
    const inv = row.invoice;
    if (!inv?.id) return;
    try {
      const r = await axios.put(`${API}/facturi/${inv.id}`, patch);
      setRows((prev) => prev.map((x) => x._key === row._key ? { ...x, invoice: r.data } : x));
      onRefresh && onRefresh();
    } catch (e) {
      toast.error("Update eșuat: " + (e?.response?.data?.detail || e.message));
    }
  };

  // Toggle paid ↔ unpaid on an imported invoice. When flipping to unpaid we
  // reset paid_amount to 0 so the "rest de plată" columns everywhere line up.
  const togglePaid = async (row, newStatus) => {
    const inv = row.invoice;
    if (!inv?.id) return;
    const patch = newStatus === "paid"
      ? { payment_status: "paid", paid_amount: Number(inv.valoare_totala || 0) }
      : { payment_status: "unpaid", paid_amount: 0 };
    await editRow(row, patch);
  };

  const imported = rows.filter((r) => r.ok);
  const failed = rows.filter((r) => !r.ok);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="w-full max-w-5xl max-h-[90vh] overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-950 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-zinc-800 px-6 py-4">
          <div>
            <h3 className="text-lg font-semibold text-zinc-100">Rezumat import facturi</h3>
            <p className="mt-0.5 text-[11px] text-zinc-500">
              {data.country} · facturile păstrează moneda originală · {data.imported}/{data.total_files} importate cu succes · {data.failed} erori · <span className="text-emerald-400">default: Plătită</span> — poți schimba pe rând
            </p>
          </div>
          <button onClick={onClose} data-testid="import-summary-close" className="rounded-md p-2 text-zinc-500 hover:bg-zinc-900 hover:text-zinc-100"><X size={18} /></button>
        </div>
        <div className="max-h-[75vh] overflow-y-auto p-4 space-y-4">
          {imported.length > 0 && (
            <div>
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-emerald-400">✓ Importate ({imported.length})</div>
              <table className="w-full text-xs">
                <thead className="bg-zinc-900/60 text-zinc-500">
                  <tr>
                    <th className="px-2 py-1.5 text-left">PDF</th>
                    <th className="px-2 py-1.5 text-left">Nr</th>
                    <th className="px-2 py-1.5 text-left">Furnizor</th>
                    <th className="px-2 py-1.5 text-left">Data</th>
                    <th className="px-2 py-1.5 text-left">Scadență</th>
                    <th className="px-2 py-1.5 text-right">Fără TVA</th>
                    <th className="px-2 py-1.5 text-right">TVA</th>
                    <th className="px-2 py-1.5 text-right">Total</th>
                    <th className="px-2 py-1.5 text-center">Status</th>
                    <th className="px-2 py-1.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {imported.map((row) => {
                    const inv = row.invoice;
                    const isPaid = (inv.payment_status || "paid") === "paid";
                    return (
                      <tr key={row._key} className="border-t border-zinc-900 hover:bg-zinc-900/30" data-testid={`import-row-${inv.id}`}>
                        <td className="px-2 py-1.5 text-zinc-400 truncate max-w-[120px]" title={row.filename}>{row.filename}</td>
                        <td className="px-2 py-1.5 font-mono text-zinc-300">
                          <input defaultValue={inv.numar_factura || ""} onBlur={(e) => e.target.value !== inv.numar_factura && editRow(row, { numar_factura: e.target.value })}
                            className="w-24 rounded bg-zinc-900 px-1.5 py-0.5 text-[11px] text-zinc-100 focus:outline-none focus:ring-1 focus:ring-cyan-500/40" />
                        </td>
                        <td className="px-2 py-1.5 text-zinc-100">
                          <input defaultValue={inv.nume || ""} onBlur={(e) => e.target.value !== inv.nume && editRow(row, { nume: e.target.value })}
                            className="w-full min-w-[140px] rounded bg-zinc-900 px-1.5 py-0.5 text-[11px] text-zinc-100 focus:outline-none focus:ring-1 focus:ring-cyan-500/40" />
                        </td>
                        <td className="px-2 py-1.5 text-zinc-400">
                          <input type="date" defaultValue={(inv.data || "").slice(0, 10)} onBlur={(e) => e.target.value !== inv.data && editRow(row, { data: e.target.value })}
                            className="rounded bg-zinc-900 px-1.5 py-0.5 text-[11px] text-zinc-100 focus:outline-none focus:ring-1 focus:ring-cyan-500/40" />
                        </td>
                        <td className="px-2 py-1.5 text-zinc-400">
                          <input type="date" defaultValue={(inv.data_scadenta || "").slice(0, 10)} onBlur={(e) => e.target.value !== inv.data_scadenta && editRow(row, { data_scadenta: e.target.value })}
                            data-testid={`import-due-date-${inv.id}`}
                            className="rounded bg-zinc-900 px-1.5 py-0.5 text-[11px] text-zinc-100 focus:outline-none focus:ring-1 focus:ring-cyan-500/40" />
                        </td>
                        <td className="px-2 py-1.5 text-right text-zinc-300 tabular-nums">
                          {Number(inv.valoare_fara_tva || 0).toFixed(2)}
                        </td>
                        <td className="px-2 py-1.5 text-right text-amber-300 tabular-nums">
                          {Number(inv.tva || 0).toFixed(2)}
                        </td>
                        <td className="px-2 py-1.5 text-right font-semibold text-emerald-300 tabular-nums">
                          {Number(inv.valoare_totala || 0).toFixed(2)} <span className="text-[10px] text-zinc-500">{inv.currency}</span>
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          <div className="inline-flex overflow-hidden rounded-md border border-zinc-800 text-[10px]">
                            <button
                              onClick={() => !isPaid && togglePaid(row, "paid")}
                              data-testid={`import-status-paid-${inv.id}`}
                              className={`px-2 py-0.5 transition ${isPaid ? "bg-emerald-500/20 text-emerald-200 font-semibold" : "bg-zinc-950 text-zinc-500 hover:bg-zinc-900"}`}
                              title="Marchează Plătită">
                              Plătită
                            </button>
                            <button
                              onClick={() => isPaid && togglePaid(row, "unpaid")}
                              data-testid={`import-status-unpaid-${inv.id}`}
                              className={`px-2 py-0.5 transition ${!isPaid ? "bg-rose-500/20 text-rose-200 font-semibold" : "bg-zinc-950 text-zinc-500 hover:bg-zinc-900"}`}
                              title="Marchează Neplătită">
                              Neplătită
                            </button>
                          </div>
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          <button onClick={() => deleteRow(row)} data-testid={`import-delete-${inv.id}`}
                            className="rounded p-1 text-zinc-500 hover:bg-rose-500/10 hover:text-rose-400" title="Șterge (nu era corect)">
                            <Trash2 size={12} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {failed.length > 0 && (
            <div>
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-rose-400">✗ Eșuate ({failed.length})</div>
              <div className="space-y-1.5">
                {failed.map((row) => (
                  <div key={row._key} className="rounded border border-rose-900/40 bg-rose-950/20 px-3 py-2 text-[11px]">
                    <div className="font-medium text-rose-200 truncate">{row.filename}</div>
                    <div className="text-rose-300/70">{row.error}</div>
                    {row.extracted && <div className="mt-1 text-[10px] text-zinc-500 font-mono">Parțial: {JSON.stringify(row.extracted)}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
          {rows.length === 0 && (
            <div className="rounded border border-dashed border-zinc-800 p-6 text-center text-[11px] text-zinc-500">Nicio factură în rezultat.</div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-zinc-800 px-6 py-3">
          <button onClick={onClose} data-testid="import-summary-done"
            className="rounded-md bg-cyan-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-cyan-500">Gata</button>
        </div>
      </div>
    </div>
  );
}

// ─── Pay VAT button ──────────────────────────────────────────────
// Înregistrează o plată efectivă a TVA-ului către stat. Se scade din
// "TVA total de plătit" în summary.
function PayVatButton({ country, onSaved }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [paidAt, setPaidAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const n = parseFloat(amount);
    if (!Number.isFinite(n) || n <= 0) { toast.error("Suma trebuie să fie > 0"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/vat-payments`, { country, amount: n, paid_at: paidAt, note });
      toast.success(`Plată TVA ${n} înregistrată pentru ${country}`);
      setOpen(false); setAmount(""); setNote("");
      onSaved && onSaved();
    } catch (e) {
      toast.error("Eroare: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        data-testid={`pay-vat-btn-${country}`}
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm font-medium text-emerald-200 transition hover:bg-emerald-500/20"
      >
        <CreditCard size={14} /> Plătește TVA
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setOpen(false)}>
          <div className="w-full max-w-md rounded-2xl border border-zinc-800 bg-zinc-950 p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-base font-semibold text-zinc-100">Înregistrează plată TVA ({country})</h3>
              <button onClick={() => setOpen(false)} className="rounded p-1 text-zinc-500 hover:bg-zinc-900 hover:text-zinc-100"><X size={16} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-xs text-zinc-400">Sumă achitată ({COUNTRY_CUR[country]})</label>
                <input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
                  data-testid={`pay-vat-amount-${country}`} autoFocus
                  className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 focus:border-emerald-500/50 focus:outline-none" />
              </div>
              <div>
                <label className="mb-1 block text-xs text-zinc-400">Data plății</label>
                <input type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)}
                  data-testid={`pay-vat-date-${country}`}
                  className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 focus:border-emerald-500/50 focus:outline-none" />
              </div>
              <div>
                <label className="mb-1 block text-xs text-zinc-400">Notă (opțional)</label>
                <input value={note} onChange={(e) => setNote(e.target.value)}
                  data-testid={`pay-vat-note-${country}`}
                  placeholder="Ex: OP nr 12345 / Bancă Transilvania"
                  className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 focus:border-emerald-500/50 focus:outline-none" />
              </div>
            </div>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button onClick={() => setOpen(false)} className="rounded px-3 py-1.5 text-sm text-zinc-400 hover:text-zinc-100">Anulează</button>
              <button onClick={submit} disabled={busy} data-testid={`pay-vat-save-${country}`}
                className="rounded-md bg-emerald-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-emerald-500 disabled:opacity-50">
                {busy ? "..." : "Înregistrează"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Paid invoices monthly report download ──────────────────────
function PaidReportButton({ country, year, month }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/facturi/paid-report`, {
        params: { country, year, month },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([r.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Facturi_platite_${country}_${year}-${String(month).padStart(2, "0")}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Descărcare eșuată: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };
  return (
    <button
      data-testid={`paid-report-btn-${country}`}
      onClick={download}
      disabled={busy}
      className="inline-flex items-center gap-2 rounded-md border border-sky-500/40 bg-sky-500/10 px-3 py-2 text-sm font-medium text-sky-200 transition hover:bg-sky-500/20 disabled:opacity-50"
      title="Descarcă raport Excel cu toate facturile plătite ale lunii"
    >
      <FileDown size={14} /> {busy ? "..." : "Raport lună (Excel)"}
    </button>
  );
}

// ─── Suppliers manage modal ─────────────────────────────────────
function SuppliersManageButton({ country, onChanged }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/suppliers`, { params: { country } });
      setItems(r.data?.items || []);
    } catch (_e) { /* silent */ } finally { setLoading(false); }
  }, [country]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const rename = async (row) => {
    const nn = window.prompt(`Redenumește "${row.nume}" pentru toate facturile:`, row.nume);
    if (!nn || nn === row.nume) return;
    try {
      const r = await axios.put(`${API}/suppliers/rename`, { old_name: row.nume, new_name: nn.trim() });
      toast.success(`${r.data?.updated} facturi actualizate`);
      load(); onChanged && onChanged();
    } catch (e) { toast.error("Eroare: " + (e?.response?.data?.detail || e.message)); }
  };

  const hide = async (row) => {
    if (!window.confirm(`Ascunzi "${row.nume}" din sugestii? (Facturile istorice rămân neatinse)`)) return;
    try {
      await axios.delete(`${API}/suppliers`, { params: { name: row.nume } });
      load(); onChanged && onChanged();
    } catch (e) { toast.error("Eroare: " + (e?.response?.data?.detail || e.message)); }
  };

  return (
    <>
      <button
        data-testid={`suppliers-manage-${country}`}
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm font-medium text-zinc-300 transition hover:border-zinc-700"
      >
        <Users size={14} /> Furnizori
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setOpen(false)}>
          <div className="w-full max-w-lg max-h-[85vh] overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-950 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-zinc-800 px-6 py-4">
              <h3 className="text-base font-semibold text-zinc-100">Furnizori — {country}</h3>
              <button onClick={() => setOpen(false)} className="rounded p-1 text-zinc-500 hover:bg-zinc-900 hover:text-zinc-100"><X size={16} /></button>
            </div>
            <div className="max-h-[65vh] overflow-y-auto p-4">
              {loading ? (
                <div className="text-center text-xs text-zinc-500">Se încarcă...</div>
              ) : items.length === 0 ? (
                <div className="text-center text-xs text-zinc-500">Niciun furnizor.</div>
              ) : (
                <div className="space-y-1">
                  {items.map((s) => (
                    <div key={s.nume} className="flex items-center justify-between rounded border border-zinc-900 bg-zinc-950 px-3 py-2">
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm text-zinc-100">{s.nume}</div>
                        <div className="text-[10px] text-zinc-500">{s.count} facturi · ultimul: {(s.last_used || "").slice(0, 10)}</div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => rename(s)} data-testid={`supplier-rename-${s.nume}`}
                          className="rounded p-1 text-zinc-500 hover:bg-zinc-900 hover:text-zinc-100" title="Redenumește">
                          <Pencil size={12} />
                        </button>
                        <button onClick={() => hide(s)} data-testid={`supplier-hide-${s.nume}`}
                          className="rounded p-1 text-zinc-500 hover:bg-rose-500/10 hover:text-rose-400" title="Ascunde din sugestii">
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Currency label for a country (used by PayVatButton).
const COUNTRY_CUR = { RO: "RON", BG: "EUR" };


// ─── TVA inițial (seed) button ──────────────────────────────────
// Ex-tab "TVA inițial" din vechea versiune — moved 2026-02-04 into a small
// gear-style button pe cardul de summary. Deschide un popup cu inputul
// numeric + explicații. TVA-ul inițial e o valoare SEED care se aplică o
// singură dată, la începutul folosirii. De la luna următoare încolo, TVA
// inițial de pe summary card devine automat leftover-ul din luna
// precedentă (carryforward chain calculat în backend).
function InitialSeedButton({ country, seedValue, seedMonth, onSaved }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(String(seedValue ?? 0));
  const [seedMonthInput, setSeedMonthInput] = useState(seedMonth || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setValue(String(seedValue ?? 0));
      setSeedMonthInput(seedMonth || "");
    }
  }, [open, seedValue, seedMonth]);

  const submit = async () => {
    const v = parseFloat(value);
    if (!Number.isFinite(v)) { toast.error("Valoare invalidă"); return; }
    setBusy(true);
    try {
      const payload = { country, tva_initial: v };
      if (seedMonthInput) payload.carryforward_start_month = seedMonthInput;
      await axios.put(`${API}/settings`, payload);
      toast.success("TVA inițial (seed) salvat");
      onSaved && onSaved();
      setOpen(false);
    } catch (e) {
      toast.error("Eroare: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        data-testid={`initial-seed-btn-${country}`}
        onClick={() => setOpen(true)}
        title="TVA inițial (seed) — folosit doar la începutul folosirii aplicației"
        className="rounded p-1 text-zinc-500 transition hover:bg-zinc-800 hover:text-zinc-200"
      >
        <Pencil size={12} />
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setOpen(false)}>
          <div className="w-full max-w-md rounded-2xl border border-zinc-800 bg-zinc-950 p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-base font-semibold text-zinc-100">TVA inițial (seed) — {country}</h3>
              <button onClick={() => setOpen(false)} className="rounded p-1 text-zinc-500 hover:bg-zinc-900 hover:text-zinc-100"><X size={16} /></button>
            </div>
            <div className="space-y-3">
              <div className="rounded border border-zinc-800 bg-zinc-900/40 p-3 text-[11px] text-zinc-400">
                <p><span className="font-medium text-zinc-200">La ce folosește?</span></p>
                <p className="mt-1">Se aplică <span className="font-medium">o singură lună</span> — cea pe care o setezi ca „luna de start". Restul lunilor pornesc de la 0 (fără carryforward). Dacă adaugi facturi retroactive pe luna de start, doar acea lună se schimbă — restul rămân independente.</p>
                <p className="mt-2">• pozitiv → de recuperat din trecut (reduce TVA de plată)</p>
                <p>• negativ → de plătit din trecut (crește TVA de plată)</p>
              </div>
              <div>
                <label className="mb-1 block text-xs text-zinc-400">Valoare seed ({COUNTRY_CUR[country]})</label>
                <input type="number" step="0.01" value={value} onChange={(e) => setValue(e.target.value)}
                  data-testid={`initial-seed-value-${country}`} autoFocus
                  className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 focus:border-sky-500/50 focus:outline-none" />
              </div>
              <div>
                <label className="mb-1 block text-xs text-zinc-400">Luna de start (carryforward)</label>
                <input type="month" value={seedMonthInput} onChange={(e) => setSeedMonthInput(e.target.value)}
                  data-testid={`initial-seed-month-${country}`}
                  className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 focus:border-sky-500/50 focus:outline-none" />
                <div className="mt-1 text-[10px] text-zinc-500">Seed-ul se aplică la această lună — de la luna următoare, backend-ul preia leftover-ul.</div>
              </div>
            </div>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button onClick={() => setOpen(false)} className="rounded px-3 py-1.5 text-sm text-zinc-400 hover:text-zinc-100">Anulează</button>
              <button onClick={submit} disabled={busy} data-testid={`initial-seed-save-${country}`}
                className="rounded-md bg-sky-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-sky-500 disabled:opacity-50">
                <Save size={14} className="mr-1 inline" /> {busy ? "..." : "Salvează"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


// ─── Month navigator ──────────────────────────────────────────────
function MonthPicker({ year, month, onChange }) {
  const prev = () => {
    let y = year, m = month - 1;
    if (m < 1) { m = 12; y -= 1; }
    onChange(y, m);
  };
  const next = () => {
    let y = year, m = month + 1;
    if (m > 12) { m = 1; y += 1; }
    onChange(y, m);
  };
  return (
    <div className="flex items-center gap-2 rounded-md border border-zinc-800 bg-zinc-900 px-1">
      <button onClick={prev} className="rounded p-1 text-zinc-400 hover:bg-zinc-800" data-testid="month-prev">
        <ChevronLeft size={16} />
      </button>
      <span className="min-w-[140px] text-center text-sm font-medium text-zinc-100" data-testid="month-label">
        {MONTHS_RO[month - 1]} {year}
      </span>
      <button onClick={next} className="rounded p-1 text-zinc-400 hover:bg-zinc-800" data-testid="month-next">
        <ChevronRight size={16} />
      </button>
    </div>
  );
}

// ─── RO Page ──────────────────────────────────────────────────────
function RomaniaView() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [summary, setSummary] = useState(null);
  const [facturi, setFacturi] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingBill, setEditingBill] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      const [s, f] = await Promise.all([
        axios.get(`${API}/summary`, { params: { country: "RO", year, month } }),
        axios.get(`${API}/facturi`, { params: { country: "RO", year, month } }),
      ]);
      setSummary(s.data);
      setFacturi(f.data.items || []);
    } catch (e) {
      toast.error("Eroare încărcare: " + (e.response?.data?.detail || e.message));
    }
  }, [year, month]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const deleteFactura = async (id) => {
    if (!window.confirm("Ștergi factura?")) return;
    try {
      await axios.delete(`${API}/facturi/${id}`);
      toast.success("Factură ștearsă");
      loadAll();
    } catch (e) {
      toast.error("Eroare: " + (e.response?.data?.detail || e.message));
    }
  };

  const bulkActionFacturi = async (action, ids) => {
    if (!ids || !ids.length) return;
    const labels = { delete: "Șterse", mark_paid: "Marcate plătite", mark_unpaid: "Marcate neplătite" };
    if (action === "delete" && !window.confirm(`Ștergi ${ids.length} facturi selectate?`)) return;
    try {
      const r = await axios.post(`${API}/facturi/bulk-action`, { ids, action });
      toast.success(`${labels[action] || action}: ${r.data?.affected || 0} facturi`);
      loadAll();
    } catch (e) {
      toast.error("Eroare bulk: " + (e.response?.data?.detail || e.message));
    }
  };


  const editFactura = (f) => { setEditingBill(f); setModalOpen(true); };
  const payFactura = async (f) => {
    const choice = window.prompt(
      `Marchează factura #${f.numar_factura || f.id.slice(0, 8)} (${f.nume})\n\n` +
      `Total: ${Number(f.valoare_totala || 0).toFixed(2)} ${f.currency || "RON"}\n` +
      `Deja plătit: ${Number(f.paid_amount || 0).toFixed(2)} ${f.currency || "RON"}\n\n` +
      `Introdu suma achitată acum (număr) SAU tastează „tot" pentru achitare completă:`,
      "tot"
    );
    if (choice === null) return;
    const trimmed = String(choice).trim().toLowerCase();
    try {
      if (trimmed === "tot" || trimmed === "") {
        await axios.post(`${API}/facturi/${f.id}/pay`, { payment_status: "paid" });
        toast.success("Factură marcată ca plătită");
      } else {
        const amt = parseFloat(trimmed.replace(",", "."));
        if (!Number.isFinite(amt) || amt <= 0) { toast.error("Sumă invalidă"); return; }
        // Add on top of already-paid amount (partial payments accumulate).
        const cumulative = Number(f.paid_amount || 0) + amt;
        const total = Number(f.valoare_totala || 0);
        const status = cumulative >= total ? "paid" : "partial";
        await axios.post(`${API}/facturi/${f.id}/pay`, {
          payment_status: status,
          paid_amount: status === "partial" ? cumulative : undefined,
        });
        toast.success(status === "paid" ? "Factură achitată integral" : `Achitare parțială: ${cumulative.toFixed(2)} / ${total.toFixed(2)} ${f.currency || "RON"}`);
      }
      loadAll();
    } catch (e) {
      toast.error("Eroare: " + (e.response?.data?.detail || e.message));
    }
  };

  const vatRate = summary?.vat_rate || 0.21;
  const currency = summary?.currency || "RON";
  const tvaTotal = summary?.tva_total || 0;
  const tvaColor = tvaTotal > 0 ? "text-amber-400" : "text-emerald-400";

  return (
    <div className="space-y-4">
      {/* Header with month picker + summary card */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <MonthPicker year={year} month={month} onChange={(y, m) => { setYear(y); setMonth(m); }} />
        <div className="text-xs text-zinc-500">
          Cotă TVA RO: <span className="font-medium text-zinc-300">{(vatRate * 100).toFixed(0)}%</span>
        </div>
      </div>

      {/* Formula summary bar */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <SummaryCard
          label="TVA încasat (comenzi)" value={summary?.tva_incasat} tone="emerald"
          testId="summary-incasat" icon={TrendingUp}
          hint={`${summary?.count_orders_finalized || 0} finalizate − ${summary?.count_orders_stornated || 0} stornate`} />
        <SummaryCard
          label="TVA plătit (facturi)" value={summary?.tva_platit} tone="rose"
          testId="summary-platit" icon={TrendingDown}
          hint={`${summary?.count_facturi_platite || 0} facturi luna aceasta${summary?.vat_paid_to_state > 0 ? ` · + ${fmt(summary.vat_paid_to_state)} către stat` : ""}`} />
        <SummaryCard
          label="TVA inițial (doar luna de start)" value={summary?.tva_initial} tone="sky"
          testId="summary-initial" icon={Wallet}
          hint={summary?.tva_initial > 0 ? "de recuperat (setat pe luna de start)" : summary?.tva_initial < 0 ? "de plătit din trecut (setat pe luna de start)" : "0 — nu se aplică pentru luna asta"}
          trailing={<InitialSeedButton
            country="RO"
            seedValue={summary?.tva_initial_seed}
            seedMonth={summary?.tva_initial_seed_month}
            onSaved={loadAll} />} />
        <div data-testid="summary-total" className="rounded-lg border border-zinc-800 bg-gradient-to-br from-zinc-900 to-zinc-950 p-4">
          <div className="text-xs uppercase tracking-wide text-zinc-500">TVA TOTAL de plătit</div>
          <div className={`mt-2 text-2xl font-bold ${tvaColor}`}>{fmt(tvaTotal)}</div>
          <div className="mt-1 text-[10px] text-zinc-500">încasat − plătit − inițial − plăți stat</div>
        </div>
      </div>

      {/* Toolbar — buttons always visible (fost sub tab TVA plătit).
          User 2026-02-04: „vreau sa pot adauga factura cand intru in
          financiar TVA, sa nu fiu nevoit sa mai intru si in alta parte". */}
      <div className="flex flex-wrap items-center justify-end gap-2">
        <BulkXlsImport country="RO" onImported={loadAll} />
        <BulkPdfImport country="RO" onImported={loadAll} />
        <PaidReportButton country="RO" year={year} month={month} />
        <SuppliersManageButton country="RO" onChanged={loadAll} />
        <button onClick={() => setModalOpen(true)} data-testid="add-bill-btn"
          className="flex items-center gap-2 rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-500">
          <Plus size={16} /> Introdu factură nouă
        </button>
      </div>

      {/* Sub-tabs eliminate 2026-02-04 (user cerinta): Facturi RON e singurul
          continut al acestui tab. Non-RON sunt in TVA Intracomunitar. */}

      {/* History table — RON invoices only (any country). */}
      <FacturiHistory
        facturi={(facturi || []).filter((f) => String(f.currency || "RON").toUpperCase() === "RON")}
        onDelete={deleteFactura} onEdit={editFactura} onPay={payFactura} onBulkAction={bulkActionFacturi} monthKey={`${year}-${String(month).padStart(2, "0")}`} />

      <AddBillModal open={modalOpen} onClose={() => { setModalOpen(false); setEditingBill(null); }} onCreated={loadAll} vatRate={vatRate} editing={editingBill} country="RO" />
    </div>
  );
}

// ─── Intracomunitar Page (facturi non-RON, orice tara) ────────────
// User cerinta 2026-02-04: tab-ul „TVA de plătit BG" a fost redenumit
// „TVA Intracomunitar" si afiseaza TOATE facturile in moneda non-RON
// (EUR, USD, HUF, BGN etc.) indiferent de tara pe care au fost stampilate
// la import (backend pastreaza country=RO sau BG dar frontend filtreaza
// pe currency). Nu se calculeaza TVA balance — sunt reverse-charge.
function IntracomunitarView() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [facturi, setFacturi] = useState([]);
  const [summary, setSummary] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingBill, setEditingBill] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      const [ro, bg, s] = await Promise.all([
        axios.get(`${API}/facturi`, { params: { country: "RO", year, month } }),
        axios.get(`${API}/facturi`, { params: { country: "BG", year, month } }),
        axios.get(`${API}/summary/intracomunitar`, { params: { year, month } }),
      ]);
      const merged = [...(ro.data?.items || []), ...(bg.data?.items || [])];
      setFacturi(merged);
      setSummary(s.data || null);
    } catch (e) {
      toast.error("Eroare încărcare: " + (e.response?.data?.detail || e.message));
    }
  }, [year, month]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const editFactura = (f) => { setEditingBill(f); setModalOpen(true); };
  const deleteFactura = async (id) => {
    if (!window.confirm("Șterg factura?")) return;
    try {
      await axios.delete(`${API}/facturi/${id}`);
      loadAll();
    } catch (e) { toast.error("Ștergere eșuată: " + (e.response?.data?.detail || e.message)); }
  };
  const bulkActionFacturi = async (action, ids) => {
    if (!ids || !ids.length) return;
    const labels = { delete: "Șterse", mark_paid: "Marcate plătite", mark_unpaid: "Marcate neplătite" };
    if (action === "delete" && !window.confirm(`Ștergi ${ids.length} facturi selectate?`)) return;
    try {
      const r = await axios.post(`${API}/facturi/bulk-action`, { ids, action });
      toast.success(`${labels[action] || action}: ${r.data?.affected || 0} facturi`);
      loadAll();
    } catch (e) {
      toast.error("Eroare bulk: " + (e.response?.data?.detail || e.message));
    }
  };
  const payFactura = async (f) => {
    const total = Number(f.valoare_totala || 0);
    const cur = f.currency || "EUR";
    const ans = window.prompt(`Achitare pentru ${f.nume} (total ${total.toFixed(2)} ${cur}):\n\nEnter = plătit integral\nSau introdu suma parțială:`, "");
    if (ans === null) return;
    try {
      if (!ans.trim()) {
        await axios.post(`${API}/facturi/${f.id}/pay`, { payment_status: "paid" });
      } else {
        const amt = parseFloat(ans);
        if (!Number.isFinite(amt) || amt <= 0) { toast.error("Sumă invalidă"); return; }
        await axios.post(`${API}/facturi/${f.id}/pay`, { payment_status: "partial", paid_amount: amt });
      }
      toast.success("Actualizat");
      loadAll();
    } catch (e) { toast.error("Eroare: " + (e.response?.data?.detail || e.message)); }
  };

  const monthKey = `${year}-${String(month).padStart(2, "0")}`;

  // Filtreaza doar non-RON (facturile intracom).
  const nonRon = (facturi || []).filter((f) => String(f.currency || "RON").toUpperCase() !== "RON");

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <MonthPicker year={year} month={month} onChange={(y, m) => { setYear(y); setMonth(m); }} />
        <div className="text-xs text-zinc-500">
          Reverse-charge intracomunitar · TVA local: <span className="font-medium text-zinc-300">0%</span>
        </div>
      </div>

      {/* Summary bar — parity 1:1 cu tab-ul RO. TVA in EUR (pivot).
          - TVA incasat: comenzi BG+HU finalizate (21% resp 27%) → EUR.
          - TVA platit: facturi non-RON, tva convertit → EUR.
          - TVA total: incasat − platit. Fara TVA initial (nu se aplica). */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <SummaryCard
          label="TVA încasat (comenzi)" value={summary?.tva_incasat} tone="emerald"
          testId="intracom-summary-incasat" icon={TrendingUp} currency="EUR"
          hint={`${summary?.count_orders_finalized || 0} finalizate · BG 21% + HU 27%`} />
        <SummaryCard
          label="TVA plătit (facturi)" value={summary?.tva_platit} tone="rose"
          testId="intracom-summary-platit" icon={TrendingDown} currency="EUR"
          hint={`${summary?.count_facturi_platite || 0} facturi non-RON luna aceasta`} />
        <div data-testid="intracom-summary-total" className="rounded-lg border border-zinc-800 bg-gradient-to-br from-zinc-900 to-zinc-950 p-4">
          <div className="text-xs uppercase tracking-wide text-zinc-500">TVA TOTAL de plătit</div>
          <div className={`mt-2 text-2xl font-bold ${(summary?.tva_total || 0) > 0 ? "text-amber-400" : "text-emerald-400"}`}>{fmt(summary?.tva_total || 0, "EUR")}</div>
          <div className="mt-1 text-[10px] text-zinc-500">încasat − plătit (pivot EUR)</div>
        </div>
      </div>

      {/* Toolbar — la fel ca RO. */}
      <div className="flex flex-wrap items-center justify-end gap-2">
        <BulkXlsImport country="RO" onImported={loadAll} />
        <BulkPdfImport country="RO" onImported={loadAll} />
        <SuppliersManageButton country="RO" onChanged={loadAll} />
        <button onClick={() => setModalOpen(true)} data-testid="intracom-add-bill-btn"
          className="flex items-center gap-2 rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-500">
          <Plus size={16} /> Factură nouă
        </button>
      </div>

      {/* Facturi Lunare — singurul tab, la fel ca in RO. */}
      <div className="flex items-center gap-1 border-b border-zinc-800">
        <div className="border-b-2 border-emerald-500 px-4 py-2 text-sm font-medium text-emerald-400">
          Facturi Lunare
        </div>
      </div>

      <FacturiHistory
        facturi={nonRon}
        onDelete={deleteFactura} onEdit={editFactura} onPay={payFactura} onBulkAction={bulkActionFacturi} monthKey={monthKey} />

      <AddBillModal open={modalOpen} onClose={() => { setModalOpen(false); setEditingBill(null); }} onCreated={loadAll} vatRate={0.0} editing={editingBill} country="RO" />
    </div>
  );
}

// ─── Summary Card ─────────────────────────────────────────────────
function SummaryCard({ label, value, tone, testId, icon: Icon, hint, currency = "RON", trailing = null }) {
  const toneMap = {
    emerald: "text-emerald-400 bg-emerald-500/10",
    rose: "text-rose-400 bg-rose-500/10",
    sky: "text-sky-400 bg-sky-500/10",
  };
  return (
    <div data-testid={testId} className="rounded-lg border border-zinc-800 bg-zinc-950 p-4">
      <div className="flex items-start justify-between">
        <div className="text-xs uppercase tracking-wide text-zinc-500">{label}</div>
        <div className="flex items-center gap-1">
          {trailing}
          {Icon && (
            <div className={`rounded-md p-1.5 ${toneMap[tone] || ""}`}>
              <Icon size={14} />
            </div>
          )}
        </div>
      </div>
      <div className={`mt-2 text-xl font-bold ${(toneMap[tone] || "text-zinc-100").split(" ")[0]}`}>{fmt(value, currency)}</div>
      {hint && <div className="mt-1 text-[10px] text-zinc-500">{hint}</div>}
    </div>
  );
}

// ─── History Table ────────────────────────────────────────────────
// Module-level so React doesn't recreate the component on every render.
function StatusPill({ f }) {
  const s = f.payment_status || "unpaid";
  if (s === "paid") return <span data-testid={`factura-status-${f.id}`} className="inline-flex items-center rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-300">Plătită</span>;
  if (s === "partial") {
    const outs = Math.max(0, Number(f.valoare_totala || 0) - Number(f.paid_amount || 0));
    return (
      <span data-testid={`factura-status-${f.id}`} className="inline-flex items-center rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] font-semibold text-amber-200"
        title={`Rest de plată: ${outs.toFixed(2)} ${f.currency || "RON"}`}>
        Parțial · rest {outs.toFixed(2)}
      </span>
    );
  }
  return <span data-testid={`factura-status-${f.id}`} className="inline-flex items-center rounded-full border border-rose-500/40 bg-rose-500/10 px-2 py-0.5 text-[10px] font-semibold text-rose-300">Neplătită</span>;
}


function FacturiHistory({ facturi, onDelete, onEdit, onPay, onBulkAction, monthKey }) {
  const PAGE_SIZE = 25;
  // Row selection is a flat Set of factura IDs across all month groups.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  // Pagination is per month group (keyed by "YYYY-MM"). Independent so
  // switching pages on one month doesn't affect another. Falls back to 1.
  const [pageByGroup, setPageByGroup] = useState({});
  // Reset selections whenever the underlying list changes (e.g. after a
  // bulk action, delete, or month change) so stale IDs never linger.
  useEffect(() => { setSelectedIds(new Set()); }, [facturi]);

  const toggleId = (id) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleAllInPage = (pageItems) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      // Only toggle rows that are actually selectable (skip import auto rows).
      const selectable = pageItems.filter((f) => !(f.source === "import" || !!f._import_id));
      const allSelected = selectable.every((f) => n.has(f.id));
      if (allSelected) {
        for (const f of selectable) n.delete(f.id);
      } else {
        for (const f of selectable) n.add(f.id);
      }
      return n;
    });
  };

  // Group by month
  const grouped = useMemo(() => {
    const g = {};
    for (const f of facturi) {
      const k = f.month || "?";
      if (!g[k]) g[k] = [];
      g[k].push(f);
    }
    // Sort keys desc
    return Object.entries(g).sort((a, b) => b[0].localeCompare(a[0]));
  }, [facturi]);

  if (grouped.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-zinc-800 bg-zinc-950 p-8 text-center">
        <Receipt className="mx-auto text-zinc-600" size={28} />
        <div className="mt-2 text-sm text-zinc-500">
          Nu ai încă facturi cu TVA înregistrate pentru luna {monthKey}.
        </div>
        <div className="mt-1 text-[11px] text-zinc-600">
          Apasă „Introdu factură nouă&quot; din tab-ul TVA plătit pentru a adăuga.
        </div>
      </div>
    );
  }

  const selectedCount = selectedIds.size;
  const runBulk = async (action) => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (onBulkAction) await onBulkAction(action, ids);
    setSelectedIds(new Set());
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs font-medium uppercase tracking-wide text-zinc-500">Istoric facturi (luna selectată)</div>
        {selectedCount > 0 && (
          <div className="flex flex-wrap items-center gap-2" data-testid="facturi-bulk-toolbar">
            <span className="text-[11px] text-zinc-400">{selectedCount} selectate</span>
            <button
              onClick={() => runBulk("mark_paid")}
              data-testid="facturi-bulk-mark-paid"
              className="rounded border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-[11px] font-medium text-emerald-200 hover:bg-emerald-500/20"
            >
              Marchează plătite
            </button>
            <button
              onClick={() => runBulk("mark_unpaid")}
              data-testid="facturi-bulk-mark-unpaid"
              className="rounded border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-[11px] font-medium text-amber-200 hover:bg-amber-500/20"
            >
              Marchează neplătite
            </button>
            <button
              onClick={() => runBulk("delete")}
              data-testid="facturi-bulk-delete"
              className="rounded border border-rose-500/40 bg-rose-500/10 px-2 py-1 text-[11px] font-medium text-rose-200 hover:bg-rose-500/20"
            >
              Șterge selectate
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              data-testid="facturi-bulk-clear"
              className="rounded border border-zinc-700 bg-zinc-800/60 px-2 py-1 text-[11px] text-zinc-300 hover:bg-zinc-800"
            >
              Anulează
            </button>
          </div>
        )}
      </div>
      {grouped.map(([mk, items]) => {
        const total = items.reduce((a, x) => a + Number(x.tva || 0), 0);
        const [y, m] = mk.split("-");
        const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
        const rawPage = pageByGroup[mk] || 1;
        const page = Math.min(Math.max(1, rawPage), totalPages);
        const startIdx = (page - 1) * PAGE_SIZE;
        const pageItems = items.slice(startIdx, startIdx + PAGE_SIZE);
        const selectable = pageItems.filter((f) => !(f.source === "import" || !!f._import_id));
        const allInPageSelected = selectable.length > 0 && selectable.every((f) => selectedIds.has(f.id));
        const goToPage = (p) => setPageByGroup((prev) => ({ ...prev, [mk]: Math.min(Math.max(1, p), totalPages) }));
        return (
          <div key={mk} className="overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950">
            <div className="flex items-center justify-between border-b border-zinc-800 bg-zinc-900/50 px-4 py-2">
              <div className="text-sm font-medium text-zinc-100">{MONTHS_RO[parseInt(m, 10) - 1]} {y}</div>
              <div className="text-xs text-zinc-400">
                {items.length} {items.length === 1 ? "factură" : "facturi"} · TVA total: <span className="font-medium text-rose-400">{fmt(total)}</span>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-900/30 text-xs uppercase text-zinc-500">
                  <tr>
                    <th className="w-8 px-2 py-2 text-center">
                      <input
                        type="checkbox"
                        aria-label="Selectează toate din pagină"
                        data-testid={`facturi-select-all-${mk}`}
                        checked={allInPageSelected}
                        disabled={selectable.length === 0}
                        onChange={() => toggleAllInPage(pageItems)}
                        className="h-3.5 w-3.5 cursor-pointer accent-emerald-500"
                      />
                    </th>
                    <th className="px-3 py-2 text-left">Data</th>
                    <th className="px-3 py-2 text-left">Nr Factură</th>
                    <th className="px-3 py-2 text-left">Nume</th>
                    <th className="px-3 py-2 text-center">Monedă</th>
                    <th className="px-3 py-2 text-right">Val. fără TVA</th>
                    <th className="px-3 py-2 text-right">TVA</th>
                    <th className="px-3 py-2 text-right">Total</th>
                    <th className="px-3 py-2 text-center">Status</th>
                    <th className="px-3 py-2 text-right">Acțiuni</th>
                  </tr>
                </thead>
                <tbody>
                  {pageItems.map((f) => {
                    const isImport = f.source === "import" || !!f._import_id;
                    const cur = f.currency || "RON";
                    // Currency badge palette — helps operators spot mixed-currency
                    // rows at a glance (before this fix the cell always rendered
                    // "RON" and the amounts appeared with the wrong currency label).
                    const curBadge = cur === "EUR"
                      ? "border-sky-500/40 bg-sky-500/10 text-sky-300"
                      : cur === "USD"
                        ? "border-violet-500/40 bg-violet-500/10 text-violet-300"
                        : "border-emerald-500/40 bg-emerald-500/10 text-emerald-300";
                    const isSelected = selectedIds.has(f.id);
                    return (
                    <tr key={f.id} className={`border-t border-zinc-800/60 hover:bg-zinc-900/30 ${isSelected ? "bg-emerald-500/5" : ""}`} data-testid={`factura-row-${f.id}`}>
                      <td className="px-2 py-2 text-center">
                        <input
                          type="checkbox"
                          data-testid={`factura-select-${f.id}`}
                          checked={isSelected}
                          disabled={isImport}
                          title={isImport ? "Facturile import se gestionează din tab-ul Importuri" : ""}
                          onChange={() => toggleId(f.id)}
                          className="h-3.5 w-3.5 cursor-pointer accent-emerald-500"
                        />
                      </td>
                      <td className="px-3 py-2 text-zinc-400">{f.data}</td>
                      <td className="px-3 py-2 font-mono text-zinc-300 text-[11px]">{f.numar_factura || "—"}</td>
                      <td className="px-3 py-2 text-zinc-100">
                        {f.nume}
                        {isImport && (
                          <span className="ml-2 inline-flex items-center rounded-full border border-sky-500/40 bg-sky-500/10 px-2 py-0.5 text-[10px] font-medium text-sky-300" data-testid="badge-import">
                            Import
                          </span>
                        )}
                        {f.detalii && (
                          <div className="mt-1 text-[10px] italic text-zinc-500" data-testid={`factura-details-${f.id}`} title={f.detalii}>
                            {f.detalii.length > 90 ? f.detalii.slice(0, 90) + "…" : f.detalii}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span
                          data-testid={`factura-currency-${f.id}`}
                          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${curBadge}`}
                        >
                          {cur}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right text-zinc-300">{fmt(f.valoare_fara_tva, cur)}</td>
                      <td className="px-3 py-2 text-right font-medium text-rose-400">{fmt(f.tva, cur)}</td>
                      <td className="px-3 py-2 text-right text-zinc-300">{fmt(f.valoare_totala, cur)}</td>
                      <td className="px-3 py-2 text-center"><StatusPill f={f} /></td>
                      <td className="px-3 py-2 text-right">
                        <div className="inline-flex items-center gap-1">
                          {isImport ? (
                            <span className="text-[10px] text-zinc-600" title="Editează din tab-ul Importuri">auto</span>
                          ) : (
                            <>
                              {(f.payment_status || "unpaid") !== "paid" && (
                                <button onClick={() => onPay(f)} data-testid={`factura-pay-${f.id}`}
                                  className="rounded border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-200 hover:bg-emerald-500/20">
                                  Marchează plătită
                                </button>
                              )}
                              <button onClick={() => onEdit(f)} data-testid={`factura-edit-${f.id}`}
                                className="rounded p-1 text-zinc-500 hover:bg-zinc-800 hover:text-zinc-100" title="Editează">
                                <Pencil size={14} />
                              </button>
                              <button onClick={() => onDelete(f.id)} data-testid={`factura-delete-${f.id}`}
                                className="rounded p-1 text-zinc-500 hover:bg-rose-500/10 hover:text-rose-400" title="Șterge">
                                <Trash2 size={14} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-zinc-800 bg-zinc-900/30 px-4 py-2 text-[11px] text-zinc-400" data-testid={`facturi-pagination-${mk}`}>
                <span>
                  Afișare {startIdx + 1}–{Math.min(startIdx + PAGE_SIZE, items.length)} din {items.length}
                </span>
                <div className="inline-flex items-center gap-1">
                  <button
                    onClick={() => goToPage(page - 1)}
                    disabled={page <= 1}
                    data-testid={`facturi-page-prev-${mk}`}
                    className="rounded border border-zinc-700 px-2 py-0.5 text-zinc-200 hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    ← Anterioară
                  </button>
                  <span className="px-2 text-zinc-300">Pagina {page} / {totalPages}</span>
                  <button
                    onClick={() => goToPage(page + 1)}
                    disabled={page >= totalPages}
                    data-testid={`facturi-page-next-${mk}`}
                    className="rounded border border-zinc-700 px-2 py-0.5 text-zinc-200 hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Următoarea →
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Cheltuieli lunare & Plăți China View ────────────────────────
// Two sub-tabs: Cheltuieli Lunare (simple {ce_reprezinta, suma, data}) and
// Plăți China (running RMB/USD debt with initial / invoice / payment kinds).
function ExpensesAndChinaView() {
  const [sub, setSub] = useState("cheltuieli"); // cheltuieli | china
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  return (
    <div className="space-y-4" data-testid="expenses-china-view">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <MonthPicker year={year} month={month} onChange={(y, m) => { setYear(y); setMonth(m); }} />
      </div>

      {/* Sub-tabs */}
      <div className="flex items-center gap-1 border-b border-zinc-800">
        <button data-testid="subtab-cheltuieli" onClick={() => setSub("cheltuieli")}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            sub === "cheltuieli"
              ? "border-b-2 border-orange-500 text-orange-400"
              : "border-b-2 border-transparent text-zinc-400 hover:text-zinc-200"
          }`}>
          Cheltuieli Lunare
        </button>
        <button data-testid="subtab-china" onClick={() => setSub("china")}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            sub === "china"
              ? "border-b-2 border-rose-500 text-rose-400"
              : "border-b-2 border-transparent text-zinc-400 hover:text-zinc-200"
          }`}>
          Plăți China
        </button>
      </div>

      {sub === "cheltuieli" ? <CheltuieliTab year={year} month={month} /> : <ChinaTab year={year} month={month} />}
    </div>
  );
}

// ─── Cheltuieli tab ────────────────────────────────────────────
function CheltuieliTab({ year, month }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [labels, setLabels] = useState([]);   // autocomplete pool
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);  // existing row when editing
  const [ce, setCe] = useState("");
  const [showSug, setShowSug] = useState(false);
  const [suma, setSuma] = useState("");
  const [data, setData] = useState(() => new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/cheltuieli`, { params: { year, month } });
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } catch (e) { toast.error("Eroare: " + (e?.response?.data?.detail || e.message)); }
  }, [year, month]);
  useEffect(() => { load(); }, [load]);

  // Fetch autocomplete labels once when the modal opens. Cache lives for the
  // whole tab session; refreshes every time the modal is reopened.
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/cheltuieli/labels`);
        setLabels(Array.isArray(r.data?.items) ? r.data.items : []);
      } catch (_e) { /* autocomplete e optional */ }
    })();
  }, [open]);

  const suggestions = useMemo(() => {
    const q = (ce || "").trim().toLowerCase();
    if (!q) return labels.slice(0, 8);
    return labels.filter((l) => (l.label || "").toLowerCase().includes(q)).slice(0, 8);
  }, [ce, labels]);

  const openCreate = () => {
    setEditing(null);
    setCe(""); setSuma(""); setNote("");
    setData(new Date().toISOString().slice(0, 10));
    setOpen(true);
  };
  const openEdit = (it) => {
    setEditing(it);
    setCe(it.ce_reprezinta || "");
    setSuma(String(it.suma ?? ""));
    setNote(it.note || "");
    setData(it.data || new Date().toISOString().slice(0, 10));
    setOpen(true);
  };

  const submit = async () => {
    const n = parseFloat(suma);
    if (!ce.trim() || !Number.isFinite(n) || n <= 0) { toast.error("Completează ce reprezintă + sumă > 0"); return; }
    setBusy(true);
    try {
      const body = { ce_reprezinta: ce.trim(), suma: n, data, note };
      if (editing) {
        await axios.put(`${API}/cheltuieli/${editing.id}`, body);
        toast.success("Cheltuială actualizată");
      } else {
        await axios.post(`${API}/cheltuieli`, body);
        toast.success("Cheltuială adăugată");
      }
      setOpen(false); setEditing(null); setCe(""); setSuma(""); setNote("");
      load();
    } catch (e) { toast.error("Eroare: " + (e?.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Șterg cheltuiala?")) return;
    try { await axios.delete(`${API}/cheltuieli/${id}`); load(); }
    catch (e) { toast.error("Eroare: " + (e?.response?.data?.detail || e.message)); }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-zinc-800 bg-zinc-950/60 p-4">
        <div>
          <div className="text-xs uppercase text-zinc-500">Total cheltuieli — {MONTHS_RO[month - 1]} {year}</div>
          <div className="mt-1 text-2xl font-bold text-orange-400" data-testid="cheltuieli-total">{fmt(total)}</div>
          <div className="text-[11px] text-zinc-500">{items.length} intrări · fără TVA, fără factură</div>
        </div>
        <button onClick={openCreate} data-testid="add-cheltuiala-btn"
          className="inline-flex items-center gap-2 rounded-md bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-500">
          <Plus size={14} /> Adaugă cheltuială
        </button>
      </div>

      <div className="rounded-lg border border-zinc-800 bg-zinc-950 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/40 text-xs uppercase text-zinc-500">
            <tr>
              <th className="px-3 py-2 text-left">Data</th>
              <th className="px-3 py-2 text-left">Ce reprezintă</th>
              <th className="px-3 py-2 text-right">Sumă</th>
              <th className="px-3 py-2 text-left">Notă</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-xs text-zinc-500">Nicio cheltuială înregistrată pentru {MONTHS_RO[month - 1]} {year}.</td></tr>
            ) : items.map((it) => (
              <tr key={it.id} className="border-t border-zinc-800/60 hover:bg-zinc-900/30" data-testid={`cheltuiala-row-${it.id}`}>
                <td className="px-3 py-2 text-zinc-400">{it.data}</td>
                <td className="px-3 py-2 text-zinc-100">{it.ce_reprezinta}</td>
                <td className="px-3 py-2 text-right font-medium text-orange-300">{fmt(it.suma, it.currency || "RON")}</td>
                <td className="px-3 py-2 text-zinc-500 text-[11px]">{it.note || "—"}</td>
                <td className="px-3 py-2 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => openEdit(it)} data-testid={`cheltuiala-edit-${it.id}`}
                      className="rounded p-1 text-zinc-500 hover:bg-orange-500/10 hover:text-orange-400" title="Editează">
                      <Pencil size={12} />
                    </button>
                    <button onClick={() => remove(it.id)} data-testid={`cheltuiala-delete-${it.id}`}
                      className="rounded p-1 text-zinc-500 hover:bg-rose-500/10 hover:text-rose-400" title="Șterge">
                      <Trash2 size={12} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setOpen(false)}>
          <div className="w-full max-w-md rounded-2xl border border-zinc-800 bg-zinc-950 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-base font-semibold text-zinc-100">
                {editing ? "Editează cheltuiala" : "Adaugă cheltuială lunară"}
              </h3>
              <button onClick={() => setOpen(false)} className="rounded p-1 text-zinc-500 hover:bg-zinc-900"><X size={16} /></button>
            </div>
            <div className="space-y-3">
              <div className="relative">
                <label className="mb-1 block text-xs text-zinc-400">Ce reprezintă</label>
                <input value={ce}
                  onChange={(e) => { setCe(e.target.value); setShowSug(true); }}
                  onFocus={() => setShowSug(true)}
                  onBlur={() => setTimeout(() => setShowSug(false), 150)}
                  data-testid="cheltuiala-desc"
                  autoComplete="off"
                  placeholder="ex: Chirie, Internet, Bonus angajat — sau alege din listă"
                  className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:border-orange-500/50" />
                {showSug && suggestions.length > 0 && (
                  <div
                    data-testid="cheltuiala-desc-suggestions"
                    className="absolute left-0 right-0 top-full z-20 mt-1 max-h-56 overflow-y-auto rounded-md border border-zinc-800 bg-zinc-950 shadow-xl"
                  >
                    {suggestions.map((s) => (
                      <button key={s.label} type="button"
                        data-testid={`cheltuiala-desc-suggestion-${s.label}`}
                        onMouseDown={(e) => { e.preventDefault(); setCe(s.label); setShowSug(false); }}
                        className="flex w-full items-center justify-between border-b border-zinc-900 px-3 py-2 text-left transition last:border-b-0 hover:bg-zinc-900/60"
                      >
                        <span className="truncate text-xs text-zinc-100">{s.label}</span>
                        <span className="ml-2 shrink-0 text-[10px] text-zinc-500">{s.count}×</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="mb-1 block text-xs text-zinc-400">Sumă (RON)</label>
                  <input type="number" step="0.01" value={suma} onChange={(e) => setSuma(e.target.value)} data-testid="cheltuiala-suma"
                    className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:border-orange-500/50" />
                </div>
                <div>
                  <label className="mb-1 block text-xs text-zinc-400">Data</label>
                  <input type="date" value={data} onChange={(e) => setData(e.target.value)} data-testid="cheltuiala-data"
                    className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:border-orange-500/50" />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-xs text-zinc-400">Notă (opțional)</label>
                <input value={note} onChange={(e) => setNote(e.target.value)}
                  className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:border-orange-500/50" />
              </div>
            </div>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button onClick={() => setOpen(false)} className="rounded px-3 py-1.5 text-sm text-zinc-400 hover:text-zinc-100">Anulează</button>
              <button onClick={submit} disabled={busy} data-testid="cheltuiala-save"
                className="rounded-md bg-orange-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-orange-500 disabled:opacity-50">
                {busy ? "..." : (editing ? "Salvează modificările" : "Adaugă")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Plăți China tab ──────────────────────────────────────────
function ChinaTab({ year, month }) {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null); // {balance:{RMB, USD}, breakdown, count}
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("payment"); // payment | invoice | initial
  const [currency, setCurrency] = useState("RMB");
  const [suma, setSuma] = useState("");
  const [data, setData] = useState(() => new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState("");
  const [ceRepr, setCeRepr] = useState("");
  const [labels, setLabels] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [l, s] = await Promise.all([
        axios.get(`${API}/plati-china`, { params: { year, month } }),
        axios.get(`${API}/plati-china/summary`),
      ]);
      setItems(l.data?.items || []);
      setSummary(s.data);
    } catch (e) { toast.error("Eroare: " + (e?.response?.data?.detail || e.message)); }
  }, [year, month]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/plati-china/labels`);
        setLabels(Array.isArray(r.data?.items) ? r.data.items : []);
      } catch (_e) { /* opt */ }
    })();
  }, [open]);

  const suggestions = useMemo(() => {
    const q = (ceRepr || "").trim().toLowerCase();
    if (!q) return labels.slice(0, 8);
    return labels.filter((l) => (l.label || "").toLowerCase().includes(q)).slice(0, 8);
  }, [ceRepr, labels]);

  const openWith = (k) => {
    setKind(k); setSuma(""); setNote(""); setCeRepr("");
    setData(new Date().toISOString().slice(0, 10));
    setEditing(null);
    setOpen(true);
  };
  const openEdit = (it) => {
    setEditing(it);
    setKind(it.kind || "payment");
    setCurrency(it.currency || "RMB");
    setSuma(String(it.suma ?? ""));
    setNote(it.note || "");
    setCeRepr(it.ce_reprezinta || "");
    setData(it.data || new Date().toISOString().slice(0, 10));
    setOpen(true);
  };

  const submit = async () => {
    const n = parseFloat(suma);
    if (!Number.isFinite(n) || n <= 0) { toast.error("Sumă > 0"); return; }
    setBusy(true);
    try {
      const body = { kind, suma: n, currency, data, note, ce_reprezinta: ceRepr.trim() || null };
      if (editing) {
        await axios.put(`${API}/plati-china/${editing.id}`, body);
        toast.success("Intrare actualizată");
      } else {
        await axios.post(`${API}/plati-china`, body);
        toast.success(`${kind === "payment" ? "Plată" : kind === "invoice" ? "Factură" : "Datorie inițială"} salvată`);
      }
      setOpen(false); setEditing(null); load();
    } catch (e) { toast.error("Eroare: " + (e?.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Șterg intrarea? (balanța se recalculează)")) return;
    try { await axios.delete(`${API}/plati-china/${id}`); load(); }
    catch (e) { toast.error("Eroare: " + (e?.response?.data?.detail || e.message)); }
  };

  const balRmb = summary?.balance?.RMB || 0;
  const balUsd = summary?.balance?.USD || 0;
  const kindLabel = { initial: "Datorie inițială", invoice: "Factură nouă", payment: "Plată" };
  const kindDotColor = { initial: "bg-amber-500", invoice: "bg-rose-500", payment: "bg-emerald-500" };

  return (
    <div className="space-y-3">
      {/* Datorie rămasă cards (RMB + USD) */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div data-testid="china-balance-rmb"
          className="rounded-lg border border-rose-500/30 bg-gradient-to-br from-rose-500/10 to-zinc-950 p-5">
          <div className="text-xs uppercase tracking-wide text-rose-300/80">Datorie rămasă · RMB</div>
          <div className={`mt-2 text-3xl font-bold ${balRmb > 0 ? "text-rose-400" : "text-emerald-400"}`}>
            {balRmb.toLocaleString("ro-RO", { maximumFractionDigits: 2 })} RMB
          </div>
          <div className="mt-1 text-[11px] text-zinc-500">
            Inițial: {summary?.breakdown?.initial?.RMB || 0} · Facturi: +{summary?.breakdown?.invoice?.RMB || 0} · Plăți: −{summary?.breakdown?.payment?.RMB || 0}
          </div>
        </div>
        <div data-testid="china-balance-usd"
          className="rounded-lg border border-zinc-800 bg-zinc-950 p-5">
          <div className="text-xs uppercase tracking-wide text-zinc-400">Datorie rămasă · USD</div>
          <div className={`mt-2 text-3xl font-bold ${balUsd > 0 ? "text-rose-400" : "text-emerald-400"}`}>
            {balUsd.toLocaleString("ro-RO", { maximumFractionDigits: 2 })} USD
          </div>
          <div className="mt-1 text-[11px] text-zinc-500">
            Inițial: {summary?.breakdown?.initial?.USD || 0} · Facturi: +{summary?.breakdown?.invoice?.USD || 0} · Plăți: −{summary?.breakdown?.payment?.USD || 0}
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-950/60 p-3">
        <button data-testid="china-add-initial" onClick={() => openWith("initial")}
          className="inline-flex items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm font-medium text-amber-200 hover:bg-amber-500/20">
          + Datorie inițială
        </button>
        <button data-testid="china-add-invoice" onClick={() => openWith("invoice")}
          className="inline-flex items-center gap-2 rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm font-medium text-rose-200 hover:bg-rose-500/20">
          + Factură nouă (mărește datoria)
        </button>
        <button data-testid="china-add-payment" onClick={() => openWith("payment")}
          className="inline-flex items-center gap-2 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm font-medium text-emerald-200 hover:bg-emerald-500/20">
          + Plată (scade datoria)
        </button>
        <div className="ml-auto text-[11px] text-zinc-500">Luna: {MONTHS_RO[month - 1]} {year}</div>
      </div>

      {/* History table */}
      <div className="rounded-lg border border-zinc-800 bg-zinc-950 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/40 text-xs uppercase text-zinc-500">
            <tr>
              <th className="px-3 py-2 text-left">Data</th>
              <th className="px-3 py-2 text-left">Tip</th>
              <th className="px-3 py-2 text-right">Sumă</th>
              <th className="px-3 py-2 text-center">Monedă</th>
              <th className="px-3 py-2 text-right">Efect</th>
              <th className="px-3 py-2 text-left">Notă</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-xs text-zinc-500">Nicio intrare China pentru {MONTHS_RO[month - 1]} {year}.</td></tr>
            ) : items.map((it) => {
              const dir = Number(it.direction || (it.kind === "payment" ? -1 : 1));
              return (
                <tr key={it.id} className="border-t border-zinc-800/60 hover:bg-zinc-900/30" data-testid={`china-row-${it.id}`}>
                  <td className="px-3 py-2 text-zinc-400">{it.data}</td>
                  <td className="px-3 py-2 text-zinc-100">
                    <span className={`inline-block h-2 w-2 rounded-full ${kindDotColor[it.kind] || "bg-zinc-500"} mr-2`}></span>
                    {kindLabel[it.kind] || it.kind}
                  </td>
                  <td className="px-3 py-2 text-right text-zinc-100">{Number(it.suma).toLocaleString("ro-RO", { maximumFractionDigits: 2 })}</td>
                  <td className="px-3 py-2 text-center">
                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${
                      it.currency === "USD"
                        ? "border-violet-500/40 bg-violet-500/10 text-violet-300"
                        : "border-rose-500/40 bg-rose-500/10 text-rose-300"
                    }`}>{it.currency}</span>
                  </td>
                  <td className={`px-3 py-2 text-right font-semibold ${dir > 0 ? "text-rose-400" : "text-emerald-400"}`}>
                    {dir > 0 ? "+" : "−"} {Number(it.suma).toLocaleString("ro-RO", { maximumFractionDigits: 2 })}
                  </td>
                  <td className="px-3 py-2 text-zinc-500 text-[11px]">
                    {it.ce_reprezinta ? <span className="text-zinc-300">{it.ce_reprezinta}</span> : null}
                    {it.ce_reprezinta && it.note ? <span className="mx-1 text-zinc-700">·</span> : null}
                    {it.note || (!it.ce_reprezinta ? "—" : null)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openEdit(it)} data-testid={`china-edit-${it.id}`}
                        className="rounded p-1 text-zinc-500 hover:bg-rose-500/10 hover:text-rose-400" title="Editează">
                        <Pencil size={12} />
                      </button>
                      <button onClick={() => remove(it.id)} data-testid={`china-delete-${it.id}`}
                        className="rounded p-1 text-zinc-500 hover:bg-rose-500/10 hover:text-rose-400" title="Șterge">
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setOpen(false)}>
          <div className="w-full max-w-md rounded-2xl border border-zinc-800 bg-zinc-950 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-base font-semibold text-zinc-100">
                {editing ? `Editează · ${kindLabel[kind]}` : `${kindLabel[kind]} · China`}
              </h3>
              <button onClick={() => setOpen(false)} className="rounded p-1 text-zinc-500 hover:bg-zinc-900"><X size={16} /></button>
            </div>
            <div className="space-y-3">
              {editing && (
                <div>
                  <label className="mb-1 block text-xs text-zinc-400">Tip intrare</label>
                  <div className="flex gap-1">
                    {[
                      { k: "initial", cls: "border-amber-500 bg-amber-500/20 text-amber-100", label: "Inițială" },
                      { k: "invoice", cls: "border-rose-500 bg-rose-500/20 text-rose-100", label: "Factură (+datorie)" },
                      { k: "payment", cls: "border-emerald-500 bg-emerald-500/20 text-emerald-100", label: "Plată (−datorie)" },
                    ].map((t) => (
                      <button key={t.k} type="button" onClick={() => setKind(t.k)}
                        data-testid={`china-modal-kind-${t.k}`}
                        className={`flex-1 rounded-md border px-2 py-1.5 text-[11px] font-medium transition ${kind === t.k ? t.cls : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:border-zinc-700"}`}>
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="relative">
                <label className="mb-1 block text-xs text-zinc-400">Ce reprezintă <span className="text-zinc-600">(dropdown din valorile anterioare sau text liber)</span></label>
                <input value={ceRepr}
                  onChange={(e) => { setCeRepr(e.target.value); setShowSug(true); }}
                  onFocus={() => setShowSug(true)}
                  onBlur={() => setTimeout(() => setShowSug(false), 150)}
                  data-testid="china-modal-cerepr"
                  autoComplete="off"
                  placeholder="ex: Alibaba PI-2026-045, Ambalaje, Materiale prima..."
                  className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:border-rose-500/50" />
                {showSug && suggestions.length > 0 && (
                  <div data-testid="china-modal-cerepr-suggestions"
                    className="absolute left-0 right-0 top-full z-20 mt-1 max-h-56 overflow-y-auto rounded-md border border-zinc-800 bg-zinc-950 shadow-xl">
                    {suggestions.map((s) => (
                      <button key={s.label} type="button"
                        data-testid={`china-modal-cerepr-suggestion-${s.label}`}
                        onMouseDown={(e) => { e.preventDefault(); setCeRepr(s.label); setShowSug(false); }}
                        className="flex w-full items-center justify-between border-b border-zinc-900 px-3 py-2 text-left transition last:border-b-0 hover:bg-zinc-900/60">
                        <span className="truncate text-xs text-zinc-100">{s.label}</span>
                        <span className="ml-2 shrink-0 text-[10px] text-zinc-500">{s.count}×</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="mb-1 block text-xs text-zinc-400">Sumă</label>
                  <input type="number" step="0.01" value={suma} onChange={(e) => setSuma(e.target.value)}
                    data-testid="china-modal-suma" autoFocus
                    className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:border-rose-500/50" />
                </div>
                <div>
                  <label className="mb-1 block text-xs text-zinc-400">Monedă</label>
                  <div className="flex gap-1">
                    {["RMB", "USD"].map((c) => (
                      <button key={c} type="button" onClick={() => setCurrency(c)}
                        data-testid={`china-modal-currency-${c}`}
                        className={`flex-1 rounded-md border px-3 py-1.5 text-sm font-medium transition ${currency === c ? "border-rose-500 bg-rose-500/20 text-rose-100" : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:border-zinc-700"}`}>
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div>
                <label className="mb-1 block text-xs text-zinc-400">Data</label>
                <input type="date" value={data} onChange={(e) => setData(e.target.value)} data-testid="china-modal-data"
                  className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:border-rose-500/50" />
              </div>
              <div>
                <label className="mb-1 block text-xs text-zinc-400">Notă (opțional)</label>
                <input value={note} onChange={(e) => setNote(e.target.value)} data-testid="china-modal-note"
                  placeholder="ex: OP către Alibaba, PI-2026-045"
                  className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:border-rose-500/50" />
              </div>
              <div className="rounded border border-zinc-800 bg-zinc-950 p-2.5 text-[11px] text-zinc-500">
                {kind === "payment" ? <>Efect: <span className="font-medium text-emerald-400">−{suma || 0} {currency}</span> scade datoria rămasă.</> :
                 kind === "invoice" ? <>Efect: <span className="font-medium text-rose-400">+{suma || 0} {currency}</span> crește datoria rămasă.</> :
                                       <>Efect: <span className="font-medium text-amber-400">+{suma || 0} {currency}</span> setează datoria inițială (se cumulează cu alte intrări &bdquo;inițială&rdquo;).</>}
              </div>
            </div>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button onClick={() => setOpen(false)} className="rounded px-3 py-1.5 text-sm text-zinc-400 hover:text-zinc-100">Anulează</button>
              <button onClick={submit} disabled={busy} data-testid="china-modal-save"
                className="rounded-md bg-rose-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-rose-500 disabled:opacity-50">
                {busy ? "..." : (editing ? "Salvează modificările" : "Salvează")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── Transport Sameday View ─────────────────────────────────────
// Report income (client shipping fees) vs Sameday expense (AWB costs).
// Filters: month | year | custom period.
function TransportView() {
  const now = new Date();
  const [rangeMode, setRangeMode] = useState("month"); // month | year | custom
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [customFrom, setCustomFrom] = useState(new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10));
  const [customTo, setCustomTo] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all"); // all | finalized | stornated
  // Sub-tab in the Transport Sameday page — comenzi vs cereri (retur/garanție/…).
  // User request 2026-08-28 to see request-level detail without switching pages.
  const [transportSubTab, setTransportSubTab] = useState("orders"); // orders | requests
  const [requestTypeFilter, setRequestTypeFilter] = useState("all"); // all | retur | garantie | schimb | livrare

  const { dateFrom, dateTo, label } = useMemo(() => {
    if (rangeMode === "year") {
      return {
        dateFrom: `${year}-01-01`,
        dateTo: `${year + 1}-01-01`,
        label: `Anul ${year}`,
      };
    }
    if (rangeMode === "custom") {
      return { dateFrom: customFrom, dateTo: customTo, label: `${customFrom} → ${customTo}` };
    }
    // month
    const nextY = month === 12 ? year + 1 : year;
    const nextM = month === 12 ? 1 : month + 1;
    return {
      dateFrom: `${year}-${String(month).padStart(2, "0")}-01`,
      dateTo: `${nextY}-${String(nextM).padStart(2, "0")}-01`,
      label: `${MONTHS_RO[month - 1]} ${year}`,
    };
  }, [rangeMode, year, month, customFrom, customTo]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/financiar/transport-report?date_from=${dateFrom}&date_to=${dateTo}`;
      const r = await axios.get(url);
      setData(r.data);
    } catch (e) {
      toast.error("Eroare la încărcarea raportului: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const sendEmailNow = async () => {
    if (!window.confirm("Trimit raportul lunar (luna precedentă) pe email + Excel atașat?")) return;
    setSendingEmail(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/financiar/transport-report/send-now`);
      toast.success("Raport trimis pe email cu Excel atașat.");
    } catch (e) {
      toast.error("Eroare: " + (e.response?.data?.detail || e.message));
    } finally { setSendingEmail(false); }
  };

  const filteredRows = useMemo(() => {
    if (!data) return [];
    const rows = data.orders.rows || [];
    if (statusFilter === "finalized") return rows.filter(r => r.status === 4);
    if (statusFilter === "stornated") return rows.filter(r => r.status === 5);
    return rows;
  }, [data, statusFilter]);

  const returnsRows = useMemo(() => {
    if (!data) return [];
    const all = data.returns_rows || [];
    if (requestTypeFilter === "all") return all;
    return all.filter((r) => r.type === requestTypeFilter);
  }, [data, requestTypeFilter]);

  const totals = data?.totals || {};
  const orders = data?.orders || {};
  const ret = data?.returns || {};
  const war = data?.warranties || {};
  const exc = data?.exchanges || {};
  const liv = data?.deliveries || {};

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-zinc-800 bg-zinc-950 p-3">
        <div className="flex items-center gap-1 rounded-md border border-zinc-800 bg-zinc-900 p-0.5">
          {[
            { id: "month", label: "Lună" },
            { id: "year", label: "An" },
            { id: "custom", label: "Perioadă" },
          ].map((b) => (
            <button key={b.id} onClick={() => setRangeMode(b.id)} data-testid={`range-${b.id}`}
              className={`rounded px-3 py-1 text-xs font-medium transition-colors ${
                rangeMode === b.id ? "bg-emerald-600 text-white" : "text-zinc-400 hover:text-zinc-200"
              }`}>{b.label}</button>
          ))}
        </div>
        {rangeMode === "month" && (
          <MonthPicker year={year} month={month} onChange={(y, m) => { setYear(y); setMonth(m); }} />
        )}
        {rangeMode === "year" && (
          <div className="flex items-center gap-2 rounded-md border border-zinc-800 bg-zinc-900 px-2">
            <button onClick={() => setYear(y => y - 1)} className="rounded p-1 text-zinc-400 hover:bg-zinc-800"><ChevronLeft size={16} /></button>
            <span className="min-w-[70px] text-center text-sm font-medium text-zinc-100">{year}</span>
            <button onClick={() => setYear(y => y + 1)} className="rounded p-1 text-zinc-400 hover:bg-zinc-800"><ChevronRight size={16} /></button>
          </div>
        )}
        {rangeMode === "custom" && (
          <div className="flex items-center gap-2">
            <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
              className="rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1 text-xs text-zinc-100"
              data-testid="custom-from" />
            <span className="text-xs text-zinc-500">→</span>
            <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
              className="rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1 text-xs text-zinc-100"
              data-testid="custom-to" />
          </div>
        )}
        <div className="ml-auto flex items-center gap-2">
          <button onClick={load} disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300 hover:bg-zinc-800"
            data-testid="btn-refresh">
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Reîncarcă
          </button>
          <button onClick={sendEmailNow} disabled={sendingEmail}
            className="inline-flex items-center gap-1.5 rounded-md bg-cyan-600/20 border border-cyan-500/40 px-3 py-1.5 text-xs font-medium text-cyan-300 hover:bg-cyan-600/30"
            data-testid="btn-send-email">
            <Send size={12} /> Trimite raport lunar acum
          </button>
        </div>
      </div>

      {/* Loading / Empty */}
      {loading && <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-10 text-center text-sm text-zinc-500">Se încarcă raportul...</div>}
      {!loading && data && (
        <>
          {/* Financial summary */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="rounded-lg border border-cyan-500/30 bg-gradient-to-br from-cyan-500/10 to-cyan-500/5 p-4">
              <div className="text-[10px] uppercase tracking-wide font-semibold text-cyan-400">Încasat de la clienți</div>
              <div className="mt-2 text-2xl font-bold font-mono text-cyan-300">+{fmt(totals.income_net || 0)}</div>
              <div className="mt-1 text-[10px] text-zinc-500">Fără TVA</div>
            </div>
            <div className="rounded-lg border border-rose-500/30 bg-gradient-to-br from-rose-500/10 to-rose-500/5 p-4">
              <div className="text-[10px] uppercase tracking-wide font-semibold text-rose-400">Plătit Sameday (total)</div>
              <div className="mt-2 text-2xl font-bold font-mono text-rose-300">−{fmt(totals.cost_sameday_total || 0)}</div>
              <div className="mt-1 text-[10px] text-zinc-500">
                Comenzi: {fmt(totals.cost_sameday_orders || 0)} · Cereri: {fmt(totals.cost_sameday_returns || 0)} · fără TVA
              </div>
            </div>
            <div className={`rounded-lg border p-4 ${(totals.net_profit || 0) >= 0 ? "border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 to-emerald-500/5" : "border-rose-500/40 bg-gradient-to-br from-rose-500/15 to-rose-500/5"}`}>
              <div className={`text-[10px] uppercase tracking-wide font-semibold ${(totals.net_profit || 0) >= 0 ? "text-emerald-400" : "text-rose-400"}`}>Profit net transport</div>
              <div className={`mt-2 text-2xl font-bold font-mono ${(totals.net_profit || 0) >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
                {(totals.net_profit || 0) >= 0 ? "+" : ""}{fmt(totals.net_profit || 0)}
              </div>
              <div className="mt-1 text-[10px] text-zinc-500">Încasat − plătit Sameday · toate fără TVA</div>
            </div>
          </div>

          {/* Requests cost boxes with counts underneath */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <CostBox icon={Package} tone="amber" label="Cost Retururi" cost={ret.awb_cost || 0} count={ret.count || 0} noun="retururi" awbCount={ret.awb_count || 0} />
            <CostBox icon={Package} tone="blue"  label="Cost Garanții" cost={war.awb_cost || 0} count={war.count || 0} noun="garanții" awbCount={war.awb_count || 0} />
            <CostBox icon={RefreshCw} tone="violet" label="Cost Colete Schimb" cost={exc.awb_cost || 0} count={exc.count || 0} noun="schimburi" awbCount={exc.awb_count || 0} />
            <CostBox icon={Truck} tone="sky" label="Cost Livrări" cost={liv.awb_cost || 0} count={liv.count || 0} noun="livrări" awbCount={liv.awb_count || 0} />
          </div>

          {/* Detailed rows — sub-tabbed: Comenzi vs Retururi & Cereri */}
          <div className="rounded-lg border border-zinc-800 bg-zinc-950 overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-zinc-800 bg-zinc-900/50 px-3 py-2">
              <div className="flex items-center gap-1 rounded-md border border-zinc-800 bg-zinc-900 p-0.5">
                <button onClick={() => setTransportSubTab("orders")} data-testid="transport-subtab-orders"
                  className={`rounded px-3 py-1 text-xs font-medium transition-colors ${
                    transportSubTab === "orders" ? "bg-emerald-600 text-white" : "text-zinc-400 hover:text-zinc-200"
                  }`}>Comenzi &amp; Taxe transport <span className="ml-1 text-[10px] opacity-70">({orders.count_finalized} + {orders.count_stornated})</span></button>
                <button onClick={() => setTransportSubTab("requests")} data-testid="transport-subtab-requests"
                  className={`rounded px-3 py-1 text-xs font-medium transition-colors ${
                    transportSubTab === "requests" ? "bg-emerald-600 text-white" : "text-zinc-400 hover:text-zinc-200"
                  }`}>Retururi &amp; Cereri <span className="ml-1 text-[10px] opacity-70">({(data.returns_rows || []).length})</span></button>
              </div>
              {transportSubTab === "orders" && (
                <div className="flex items-center gap-1 rounded-md border border-zinc-800 bg-zinc-900 p-0.5">
                  {[
                    { id: "all", label: "Toate" },
                    { id: "finalized", label: "Finalizate" },
                    { id: "stornated", label: "Stornate" },
                  ].map((b) => (
                    <button key={b.id} onClick={() => setStatusFilter(b.id)}
                      className={`rounded px-2 py-1 text-[11px] font-medium transition-colors ${
                        statusFilter === b.id ? "bg-emerald-600 text-white" : "text-zinc-400 hover:text-zinc-200"
                      }`}
                      data-testid={`filter-${b.id}`}>{b.label}</button>
                  ))}
                </div>
              )}
              {transportSubTab === "requests" && (
                <div className="flex items-center gap-1 rounded-md border border-zinc-800 bg-zinc-900 p-0.5">
                  {[
                    { id: "all", label: "Toate" },
                    { id: "retur", label: "Retur" },
                    { id: "garantie", label: "Garanție" },
                    { id: "schimb", label: "Schimb" },
                    { id: "livrare", label: "Livrare" },
                  ].map((b) => (
                    <button key={b.id} onClick={() => setRequestTypeFilter(b.id)}
                      className={`rounded px-2 py-1 text-[11px] font-medium transition-colors ${
                        requestTypeFilter === b.id ? "bg-emerald-600 text-white" : "text-zinc-400 hover:text-zinc-200"
                      }`}
                      data-testid={`request-filter-${b.id}`}>{b.label}</button>
                  ))}
                </div>
              )}
            </div>
            <div className="max-h-[500px] overflow-auto">
              {transportSubTab === "orders" ? (
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-zinc-900">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium uppercase tracking-wide text-zinc-500">Comandă</th>
                    <th className="px-3 py-2 text-left font-medium uppercase tracking-wide text-zinc-500">Data</th>
                    <th className="px-3 py-2 text-left font-medium uppercase tracking-wide text-zinc-500">Integrare</th>
                    <th className="px-3 py-2 text-left font-medium uppercase tracking-wide text-zinc-500">Client</th>
                    <th className="px-3 py-2 text-left font-medium uppercase tracking-wide text-zinc-500">Status</th>
                    <th className="px-3 py-2 text-right font-medium uppercase tracking-wide text-emerald-500/80" title="Cât a plătit clientul pentru transport">Încasat de client<br/><span className="text-[9px] font-normal normal-case text-zinc-500">fără TVA</span></th>
                    <th className="px-3 py-2 text-right font-medium uppercase tracking-wide text-rose-500/80" title="Cât am plătit noi Sameday (calcul Bragus)">Cost Sameday<br/><span className="text-[9px] font-normal normal-case text-zinc-500">fără TVA</span></th>
                    <th className="px-3 py-2 text-right font-medium uppercase tracking-wide text-sky-500/80" title="Încasat − Cost Sameday">Profit / AWB<br/><span className="text-[9px] font-normal normal-case text-zinc-500">fără TVA</span></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-900">
                  {filteredRows.length === 0 && (
                    <tr><td colSpan={8} className="p-8 text-center text-zinc-500">Nicio comandă în perioada selectată.</td></tr>
                  )}
                  {filteredRows.map((r, i) => {
                    const statusCls = r.status === 4
                      ? "text-emerald-300"
                      : r.status === 5 ? "text-rose-300" : "text-zinc-400";
                    // Feb 2026 (user request) — dacă AWB-ul încă n-are
                    // facturare Sameday din xlsx desfășurător, afișăm
                    // PENDING (portocaliu) și nu calculăm profit.
                    const isPending = r.awb_pending || r.awb_cost === null;
                    const profit = isPending ? null : Number(r.shipping_net || 0) - Number(r.awb_cost || 0);
                    const profitCls = profit === null ? "text-amber-400" : (profit >= 0 ? "text-sky-300" : "text-rose-400");
                    return (
                      <tr key={`${r.order_id}-${i}`} className="hover:bg-zinc-900/40">
                        <td className="px-3 py-1.5 font-mono font-bold text-sm text-zinc-100">#{r.order_id}</td>
                        <td className="px-3 py-1.5 text-zinc-400">{r.date}</td>
                        <td className="px-3 py-1.5 text-zinc-500 text-[10px]">{r.integration}</td>
                        <td className="px-3 py-1.5 text-zinc-300">{(r.customer || "").slice(0, 40)}</td>
                        <td className={`px-3 py-1.5 ${statusCls}`}>{r.status_label}</td>
                        <td className="px-3 py-1.5 text-right font-mono text-emerald-300">{fmt(r.shipping_net)}</td>
                        <td className="px-3 py-1.5 text-right font-mono">
                          {isPending ? (
                            <span className="rounded-md border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-300">Pending</span>
                          ) : (
                            <span className="text-rose-300">{fmt(r.awb_cost)}</span>
                          )}
                        </td>
                        <td className={`px-3 py-1.5 text-right font-mono ${profitCls}`}>
                          {profit === null ? "—" : fmt(profit)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              ) : (
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-zinc-900">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium uppercase tracking-wide text-zinc-500">Data</th>
                    <th className="px-3 py-2 text-left font-medium uppercase tracking-wide text-zinc-500">Nr. cerere</th>
                    <th className="px-3 py-2 text-left font-medium uppercase tracking-wide text-zinc-500">Tip</th>
                    <th className="px-3 py-2 text-left font-medium uppercase tracking-wide text-zinc-500">Client</th>
                    <th className="px-3 py-2 text-left font-medium uppercase tracking-wide text-zinc-500">AWB-uri</th>
                    <th className="px-3 py-2 text-right font-medium uppercase tracking-wide text-emerald-500/80" title="Cât încasezi de la client pentru transport (doar pt tip Livrare)">Încasat de client<br/><span className="text-[9px] font-normal normal-case text-zinc-500">fără TVA</span></th>
                    <th className="px-3 py-2 text-right font-medium uppercase tracking-wide text-rose-500/80" title="Cât plătim Sameday pentru AWB-urile din cerere">Cost Sameday<br/><span className="text-[9px] font-normal normal-case text-zinc-500">fără TVA</span></th>
                    <th className="px-3 py-2 text-right font-medium uppercase tracking-wide text-sky-500/80" title="Încasat − Cost Sameday">Profit / cerere<br/><span className="text-[9px] font-normal normal-case text-zinc-500">fără TVA</span></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-900">
                  {returnsRows.length === 0 && (
                    <tr><td colSpan={8} className="p-8 text-center text-zinc-500">Nicio cerere de retur/garanție/schimb/livrare în perioada selectată.</td></tr>
                  )}
                  {returnsRows.map((r) => {
                    const typeCls = {
                      retur: "text-amber-300", garantie: "text-orange-300",
                      schimb: "text-sky-300", livrare: "text-violet-300",
                    }[r.type] || "text-zinc-400";
                    // Doar la LIVRARE se incaseaza taxa de la client (colet nou → client);
                    // la retur/schimb/garantie nu se incaseaza nimic.
                    const income = r.type === "livrare" ? Number(r.total_cost || 0) : 0;
                    const cost = Number(r.total_cost || 0);
                    const profit = income - cost;
                    const profitCls = profit >= 0 ? "text-sky-300" : "text-rose-400";
                    return (
                      <tr key={r.id} className="hover:bg-zinc-900/40" data-testid={`req-row-${r.id}`}>
                        <td className="px-3 py-1.5 text-zinc-400">{r.date}</td>
                        <td className="px-3 py-1.5 font-mono font-bold text-sm text-zinc-100">#{r.request_number || r.id}</td>
                        <td className={`px-3 py-1.5 capitalize ${typeCls}`}>{r.type}</td>
                        <td className="px-3 py-1.5 text-zinc-300">{(r.customer || "").slice(0, 40)}</td>
                        <td className="px-3 py-1.5 text-zinc-400 text-[10px]">
                          <div className="space-y-0.5">
                            {r.awbs.map((a, idx) => (
                              <div key={idx} className="flex items-center gap-2">
                                <span className={`inline-block w-[68px] shrink-0 rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase ${
                                  a.kind === "pickup" ? "border border-amber-500/40 text-amber-300 bg-amber-500/5"
                                  : "border border-sky-500/40 text-sky-300 bg-sky-500/5"}`}>{a.kind}</span>
                                <span className="font-mono text-zinc-300">{a.number}</span>
                                <span className="text-zinc-500">·</span>
                                <span className="font-mono text-zinc-200">{fmt(a.cost_total)}</span>
                                {a.service_label && (
                                  <span className="ml-1 text-[9px] italic text-zinc-500">({a.service_label})</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="px-3 py-1.5 text-right font-mono text-emerald-300">{income > 0 ? fmt(income) : "—"}</td>
                        <td className="px-3 py-1.5 text-right font-mono text-rose-300">{fmt(cost)}</td>
                        <td className={`px-3 py-1.5 text-right font-mono ${profitCls}`}>{fmt(profit)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function CostBox({ icon: Icon, tone, label, cost, count, noun, awbCount }) {
  const tones = {
    amber:  { bg: "border-amber-500/30 bg-gradient-to-br from-amber-500/10 to-amber-500/5", txt: "text-amber-300", label: "text-amber-400" },
    blue:   { bg: "border-sky-500/30 bg-gradient-to-br from-sky-500/10 to-sky-500/5",       txt: "text-sky-300",   label: "text-sky-400" },
    violet: { bg: "border-violet-500/30 bg-gradient-to-br from-violet-500/10 to-violet-500/5", txt: "text-violet-300", label: "text-violet-400" },
    sky:    { bg: "border-cyan-500/30 bg-gradient-to-br from-cyan-500/10 to-cyan-500/5",     txt: "text-cyan-300",  label: "text-cyan-400" },
  }[tone] || { bg: "border-zinc-800 bg-zinc-950", txt: "text-zinc-300", label: "text-zinc-400" };
  return (
    <div className={`rounded-lg border p-4 ${tones.bg}`}>
      <div className="flex items-center gap-1.5">
        <Icon size={14} className={tones.label} />
        <div className={`text-[10px] uppercase tracking-wide font-semibold ${tones.label}`}>{label}</div>
      </div>
      <div className={`mt-2 text-xl font-bold font-mono ${tones.txt}`}>{fmt(cost)}</div>
      <div className="mt-1 text-[10px] text-zinc-500">
        pentru <b className="text-zinc-300">{count}</b> {noun}
        {awbCount > 0 && <> · {awbCount} AWB</>}
      </div>
    </div>
  );
}


// ─── Main ─────────────────────────────────────────────────────────
// ─── Status Încasare Comenzi (Etapa 2 — Gomag+Manual live) ─────────
// Etapa 2 (2026-09-02): tabul Gomag+Manual acum incarca datele reale din
// `/api/financiar/status-incasare/gomag-manual`:
//   - Ramburs → status derivat din cod_refund_status (Sameday mails zilnice)
//   - Online  → default „incasat" (Netopia import ramane pentru Etapa 2b)
// Tabul eMAG ramane placeholder — asteapta importul „Desfasurator de plata".
function StatusIncasareView() {
  const [channel, setChannel] = useState("gomag_manual"); // gomag_manual | emag
  const [emagCountry, setEmagCountry] = useState("RO"); // RO | BG | HU (doar pt eMAG)
  const [status, setStatus] = useState("neincasate"); // incasate | neincasate
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ count_incasate: 0, count_neincasate: 0, suma_incasate: 0, suma_neincasate: 0 });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());  // bulk selection of order composite_id
  const [bulkBusy, setBulkBusy] = useState(false);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const path = channel === "emag" ? "emag" : "gomag-manual";
      const params = { year, month, status: status === "plati_shopify" ? undefined : status };
      if (channel === "emag") params.country = emagCountry;
      else await axios.post(`${API}/shopify-payouts/sync`).catch(() => {});
      const r = await axios.get(`${API}/status-incasare/${path}`, { params });
      setRows(r.data?.items || []);
      setTotals(r.data?.totals || { count_incasate: 0, count_neincasate: 0, suma_incasate: 0, suma_neincasate: 0 });
    } catch (e) {
      toast.error("Eroare încărcare: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [channel, emagCountry, status, year, month]);

  useEffect(() => { loadRows(); }, [loadRows]);
  useEffect(() => { setSelected(new Set()); }, [channel, emagCountry, status, year, month]);

  const rowKey = (r) => r.composite_id || `${r.integration_key || ""}:${r.order_id}`;
  const toggleRow = (r) => {
    setSelected((prev) => {
      const n = new Set(prev);
      const k = rowKey(r);
      n.has(k) ? n.delete(k) : n.add(k);
      return n;
    });
  };
  const toggleAll = () => {
    setSelected((prev) => (prev.size === rows.length ? new Set() : new Set(rows.map(rowKey))));
  };
  const clearSelection = () => setSelected(new Set());

  const runBulkAction = async (action) => {
    const items = rows.filter((r) => selected.has(rowKey(r))).map((r) => ({
      order_id: String(r.order_id),
      integration_key: r.integration_key,
    }));
    if (items.length === 0) return;
    const label = { mark_paid: "Marchează încasate", mark_unpaid: "Marchează neîncasate" }[action] || action;
    if (!window.confirm(`${label} ${items.length} comenzi selectate?`)) return;
    setBulkBusy(true);
    try {
      const r = await axios.post(`${API}/status-incasare/order-action-bulk`, { orders: items, action });
      const d = r.data || {};
      if (d.failed_count > 0) {
        toast.warning(`${d.ok_count}/${d.total} OK · ${d.failed_count} eșuate`);
        console.warn("Bulk failures:", d.failed);
      } else {
        toast.success(`${d.ok_count} comenzi actualizate`);
      }
      clearSelection();
      loadRows();
    } catch (e) {
      toast.error("Acțiune în masă eșuată: " + (e.response?.data?.detail || e.message));
    } finally {
      setBulkBusy(false);
    }
  };

  const [netopiaBusy, setNetopiaBusy] = useState(false);
  const importNetopia = async () => {
    const reportId = window.prompt("Report ID Netopia (ex: 3201700):", "3201700");
    if (!reportId) return;
    setNetopiaBusy(true);
    try {
      const r = await axios.post(`${API}/netopia/import`, { report_id: reportId.trim() });
      toast.success(`Netopia: ${r.data?.matched || 0}/${r.data?.rows_total || 0} comenzi marcate încasate.`);
      loadRows();
    } catch (e) {
      toast.error("Netopia import eșuat: " + (e.response?.data?.detail || e.message));
    } finally {
      setNetopiaBusy(false);
    }
  };

  // Rescanare completa mail-uri Netopia + Sameday pe fereastra larga.
  // Ruleaza AMBELE loop-uri pe production (dupa un redeploy sau catch-up
  // istoric) — user cerinta 2026-09-03.
  const [rescanBusy, setRescanBusy] = useState(false);
  const rescanMails = async () => {
    const days = window.prompt("Rescanare mail-uri (Netopia + Sameday). Câte zile înapoi?", "60");
    if (!days) return;
    const d = Math.max(1, Math.min(180, parseInt(days, 10) || 60));
    setRescanBusy(true);
    try {
      const [netopiaR, samedayR] = await Promise.all([
        axios.post(`${API}/netopia/scan-mails?days=${d}`).catch((e) => ({ error: e })),
        axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/emag-vendor-rpa/cod-refunds/mail-import/scan?days=${d}`).catch((e) => ({ error: e })),
      ]);
      const nOK = !netopiaR.error;
      const sOK = !samedayR.error;
      const nScan = netopiaR.data?.scanned ?? "?";
      const sScan = samedayR.data?.scanned ?? "?";
      if (nOK && sOK) {
        toast.success(`Rescan ${d} zile: Netopia ${nScan} mail-uri · Sameday ${sScan} mail-uri procesate.`);
      } else {
        toast.error(`Rescan parțial: Netopia ${nOK ? "OK" : "EȘUAT"} · Sameday ${sOK ? "OK" : "EȘUAT"}`);
      }
      loadRows();
    } catch (e) {
      toast.error("Rescan eșuat: " + (e.response?.data?.detail || e.message));
    } finally {
      setRescanBusy(false);
    }
  };

  const [desfBusy, setDesfBusy] = useState(false);
  const desfInputRef = React.useRef(null);
  const [dvBusy, setDvBusy] = useState(false);
  const dvInputRef = React.useRef(null);

  // Actiuni manuale per rand (user cerinta 2026-09-03).
  const runOrderAction = async (row, action) => {
    const label = { hide: "ascunde", mark_paid: "marcheaza incasat", mark_unpaid: "marcheaza neincasat" }[action];
    if (!window.confirm(`Confirmi: ${label} comanda ${row.order_id}?`)) return;
    try {
      await axios.post(`${API}/status-incasare/order-action`, {
        order_id: String(row.order_id),
        integration_key: row.integration_key,
        action,
      });
      toast.success(`${label} · ${row.order_id}`);
      loadRows();
    } catch (e) {
      toast.error("Actiune esuata: " + (e.response?.data?.detail || e.message));
    }
  };
  const uploadDesfasuratoare = async (files) => {
    if (!files || files.length === 0) return;
    if (files.length > 10) {
      toast.error("Maxim 10 fișiere per încărcare.");
      return;
    }
    setDesfBusy(true);
    try {
      const fd = new FormData();
      for (const f of files) fd.append("files", f);
      const r = await axios.post(`${API}/status-incasare/emag/upload-desfasurator`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const results = r.data?.results || [];
      const totalMatched = results.reduce((a, b) => a + (b.matched || 0), 0);
      const totalRows = results.reduce((a, b) => a + (b.rows_total || 0), 0);
      const totalScanned = results.reduce((a, b) => a + (b.rows_scanned || 0), 0);
      if (totalMatched === 0 && totalRows === 0) {
        // Parser nu a gasit niciun ID de comanda -> problema de layout
        const detail = results.map(x => `${x.file}: sheets=[${(x.sheets_scanned||[]).join(', ')}] rânduri=${x.rows_scanned || 0}`).join(' · ');
        toast.error(`Niciun ID de comandă găsit în fișier. ${detail}`, { duration: 10000 });
      } else if (totalMatched === 0) {
        // Am gasit ID-uri dar nu s-au matchat cu comenzi existente
        const sample = results.flatMap(x => x.sample_ids || []).slice(0, 3).join(', ');
        toast.error(`${totalRows} ID-uri găsite dar 0 matchate în DB. Sample: ${sample}. Verifică dacă e contul eMAG corect (RO/BG/HU).`, { duration: 10000 });
      } else {
        toast.success(`Desfășurătoare: ${results.length} fișier(e) · ${totalMatched}/${totalRows} comenzi marcate (${totalScanned} rânduri scanate)`);
      }
      loadRows();
    } catch (e) {
      toast.error("Upload eșuat: " + (e.response?.data?.detail || e.message));
    } finally {
      setDesfBusy(false);
      if (desfInputRef.current) desfInputRef.current.value = "";
    }
  };

  // Upload Desfasurator VOUCHER (DV) - comenzi platite integral cu voucher.
  // Match Order ID (col E) + valoare exacta voucher (col X) = total comanda.
  // Daca voucher_value < total → skip (comanda va aparea in DP).
  const uploadDV = async (files) => {
    if (!files || files.length === 0) return;
    if (files.length > 10) {
      toast.error("Maxim 10 fișiere per încărcare.");
      return;
    }
    setDvBusy(true);
    try {
      const fd = new FormData();
      for (const f of files) fd.append("files", f);
      const r = await axios.post(`${API}/status-incasare/emag/upload-desfasurator-voucher`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const results = r.data?.results || [];
      const totalMatched = results.reduce((a, b) => a + (b.matched || 0), 0);
      const totalSkipped = results.reduce((a, b) => a + (b.skipped_mismatch || 0), 0);
      const totalRows = results.reduce((a, b) => a + (b.rows_total || 0), 0);
      const totalNotInDb = results.reduce((a, b) => a + (b.not_in_db || 0), 0);
      if (totalMatched === 0 && totalRows === 0) {
        toast.error("Niciun voucher găsit în fișier. Verifică col E (Order ID) și col X (Value of vouchers).", { duration: 10000 });
      } else if (totalMatched === 0) {
        const sample = results.flatMap(x => x.sample_skipped || []).slice(0, 3)
          .map(s => `#${s.order_id}: comanda=${s.order_total} vs voucher=${s.voucher_value}`).join(" · ");
        toast.error(`DV: 0 comenzi marcate. ${totalSkipped} sărite (voucher parțial) · ${totalNotInDb} necunoscute. Sample: ${sample}`, { duration: 12000 });
      } else {
        toast.success(`DV: ${totalMatched}/${totalRows} comenzi marcate încasate integral cu voucher${totalSkipped ? ` · ${totalSkipped} sărite (voucher parțial, vor apărea în DP)` : ""}`);
      }
      loadRows();
    } catch (e) {
      toast.error("Upload DV eșuat: " + (e.response?.data?.detail || e.message));
    } finally {
      setDvBusy(false);
      if (dvInputRef.current) dvInputRef.current.value = "";
    }
  };

  const channelTabs = [
    { id: "gomag_manual", label: "Shopify + Manual", testId: "incasare-tab-shopify-manual" },
    { id: "emag", label: "eMAG", testId: "incasare-tab-emag" },
  ];
  const statusTabs = [
    { id: "incasate", label: `Încasate (${totals.count_incasate || 0})`, testId: "incasare-status-incasate", tone: "emerald" },
    { id: "neincasate", label: `Neîncasate (${totals.count_neincasate || 0})`, testId: "incasare-status-neincasate", tone: "rose" },
    ...(channel === "emag" ? [] : [{ id: "plati_shopify", label: "Plăți Shopify", testId: "incasare-status-plati-shopify", tone: "emerald" }]),
  ];
  // Subtabs eMAG per tara: RO/BG/HU cu steaguri (user cerinta 2026-09-03).
  const emagCountries = [
    { id: "RO", flag: "🇷🇴", label: "eMAG RO", testId: "incasare-emag-country-ro" },
    { id: "BG", flag: "🇧🇬", label: "eMAG BG", testId: "incasare-emag-country-bg" },
    { id: "HU", flag: "🇭🇺", label: "eMAG HU", testId: "incasare-emag-country-hu" },
  ];
  const flagFor = (integKey) => {
    const k = String(integKey || "").toLowerCase();
    if (k.startsWith("emag_ro")) return "🇷🇴";
    if (k.startsWith("emag_bg")) return "🇧🇬";
    if (k.startsWith("emag_hu")) return "🇭🇺";
    return "";
  };
  const platformLabel = (integKey) => {
    const k = String(integKey || "").toLowerCase();
    if (k === "emag_ro_bau") return "eMAG RO BAU";
    if (k === "emag_ro_fbe") return "eMAG RO FBE";
    if (k === "emag_bg_bau") return "eMAG BG BAU";
    if (k === "emag_bg_fbe") return "eMAG BG FBE";
    if (k === "emag_hu_bau") return "eMAG HU BAU";
    if (k === "emag_hu_fbe") return "eMAG HU FBE";
    if (k === "gomag_bragus") return "Gomag";
    if (k === "manual_bragus") return "Manual";
    return integKey || "";
  };

  return (
    <div className="space-y-4" data-testid="status-incasare-view">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <MonthPicker year={year} month={month} onChange={(y, m) => { setYear(y); setMonth(m); }} />
        <div className="flex items-center gap-3">
          {channel === "gomag_manual" && (
            <>
              <button onClick={rescanMails} disabled={rescanBusy} data-testid="netopia-rescan-mails-btn"
                title="Scanează inbox pentru mail-uri Netopia + Sameday și marchează comenzile încasate. Rulează după deploy sau când vezi comenzi neîncasate în mod eronat."
                className="inline-flex items-center gap-2 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-xs font-medium text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-50">
                {rescanBusy ? <RefreshCw size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                {rescanBusy ? "Se rescanează..." : "Rescanare mailuri"}
              </button>
              <button onClick={importNetopia} disabled={netopiaBusy} data-testid="netopia-import-btn"
                className="inline-flex items-center gap-2 rounded-md border border-sky-500/40 bg-sky-500/10 px-3 py-1.5 text-xs font-medium text-sky-200 hover:bg-sky-500/20 disabled:opacity-50">
                {netopiaBusy ? <RefreshCw size={14} className="animate-spin" /> : <Upload size={14} />}
                {netopiaBusy ? "Se importă..." : "Importă raport Netopia"}
              </button>
            </>
          )}
          {channel === "emag" && (
            <>
              <input ref={desfInputRef} type="file" multiple accept=".xlsx"
                className="hidden" data-testid="emag-desf-input"
                onChange={(e) => uploadDesfasuratoare(Array.from(e.target.files || []))} />
              <button onClick={() => desfInputRef.current?.click()} disabled={desfBusy}
                data-testid="emag-desf-upload-btn"
                title="Desfășurător de Plăți — comenzile decontate normal de eMAG"
                className="inline-flex items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-xs font-medium text-amber-200 hover:bg-amber-500/20 disabled:opacity-50">
                {desfBusy ? <RefreshCw size={14} className="animate-spin" /> : <Upload size={14} />}
                {desfBusy ? "Se încarcă..." : "Încarcă DP"}
              </button>
              <input ref={dvInputRef} type="file" multiple accept=".xlsx"
                className="hidden" data-testid="emag-dv-input"
                onChange={(e) => uploadDV(Array.from(e.target.files || []))} />
              <button onClick={() => dvInputRef.current?.click()} disabled={dvBusy}
                data-testid="emag-dv-upload-btn"
                title="Desfășurător Voucher — comenzile plătite integral cu voucher (nu apar în DP). Match strict: col X = total comandă exact."
                className="inline-flex items-center gap-2 rounded-md border border-fuchsia-500/40 bg-fuchsia-500/10 px-3 py-1.5 text-xs font-medium text-fuchsia-200 hover:bg-fuchsia-500/20 disabled:opacity-50">
                {dvBusy ? <RefreshCw size={14} className="animate-spin" /> : <Upload size={14} />}
                {dvBusy ? "Se încarcă..." : "Încarcă DV"}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Summary cards — user cerinta 2026-09-02: card "Valoare comenzi
          neîncasate" pentru ambele canale + suplimentar pt eMAG cardul
          "De încasat după comision" (real, dupa deduceri Genius/Ads/Ff/Com).
          Moneda cardurilor eMAG se schimba dupa subtab (RON/EUR/HUF) —
          user cerinta 2026-09-03. */}
      <div className={`grid grid-cols-1 gap-3 sm:grid-cols-2 ${channel === "emag" ? "lg:grid-cols-3" : "lg:grid-cols-2"}`}>
        <div className="rounded-lg border border-rose-500/40 bg-rose-500/5 p-4" data-testid="incasare-card-neincasate">
          <div className="text-xs uppercase tracking-wide text-rose-300">Valoare comenzi neîncasate</div>
          <div className="mt-2 text-2xl font-bold text-rose-400">{fmt(totals.suma_neincasate, totals.currency || "RON")}</div>
          <div className="mt-1 text-[10px] text-zinc-500">{totals.count_neincasate || 0} comenzi · orice metodă de plată</div>
          {channel !== "emag" && (totals.suma_neincasate_ramburs != null || totals.suma_neincasate_card != null) && (
            <div className="mt-3 space-y-1 border-t border-rose-500/20 pt-2">
              <div className="flex items-center justify-between gap-2" data-testid="incasare-card-neincasate-ramburs">
                <span className="text-[10px] uppercase tracking-wide text-amber-300/80">Ramburs</span>
                <span className="tabular-nums text-xs font-semibold text-amber-300">
                  {fmt(totals.suma_neincasate_ramburs || 0, totals.currency || "RON")}
                  <span className="ml-1 text-[9px] font-normal text-zinc-500">({totals.count_neincasate_ramburs || 0})</span>
                </span>
              </div>
              <div className="flex items-center justify-between gap-2" data-testid="incasare-card-neincasate-card">
                <span className="text-[10px] uppercase tracking-wide text-sky-300/80">Card / online</span>
                <span className="tabular-nums text-xs font-semibold text-sky-300">
                  {fmt(totals.suma_neincasate_card || 0, totals.currency || "RON")}
                  <span className="ml-1 text-[9px] font-normal text-zinc-500">({totals.count_neincasate_card || 0})</span>
                </span>
              </div>
            </div>
          )}
        </div>
        <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-4" data-testid="incasare-card-incasate">
          <div className="text-xs uppercase tracking-wide text-emerald-300">Valoare comenzi încasate</div>
          <div className="mt-2 text-2xl font-bold text-emerald-400">{fmt(totals.suma_incasate, totals.currency || "RON")}</div>
          <div className="mt-1 text-[10px] text-zinc-500">{totals.count_incasate || 0} comenzi</div>
        </div>
        {channel === "emag" && (
          <div className="rounded-lg border border-sky-500/40 bg-sky-500/5 p-4" data-testid="incasare-card-real-neincasate"
               title="Total sumă − taxa Genius (8 RON dacă e cazul) − Fulfillment (5 RON/unit) − Ads % − Comision eMAG %">
            <div className="text-xs uppercase tracking-wide text-sky-300">De încasat după comision</div>
            <div className="mt-2 text-2xl font-bold text-sky-400">{fmt(totals.real_neincasate, totals.currency || "RON")}</div>
            <div className="mt-1 text-[10px] text-zinc-500">real: total − Genius − FF − Ads − Comision</div>
          </div>
        )}
      </div>

      {/* Channel subtabs */}
      <div className="flex items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-950 p-1">
        {channelTabs.map((t) => (
          <button key={t.id} onClick={() => setChannel(t.id)} data-testid={t.testId}
            className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
              channel === t.id ? "bg-emerald-600 text-white" : "text-zinc-400 hover:text-zinc-100"
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* eMAG country subtabs — vizibile doar cand channel === emag. */}
      {channel === "emag" && (
        <div className="flex items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-900/50 p-1">
          {emagCountries.map((c) => (
            <button key={c.id} onClick={() => setEmagCountry(c.id)} data-testid={c.testId}
              className={`flex-1 rounded-md px-4 py-1.5 text-sm font-medium transition-colors inline-flex items-center justify-center gap-2 ${
                emagCountry === c.id ? "bg-amber-600 text-white" : "text-zinc-400 hover:text-zinc-100"
              }`}>
              <span className="text-base">{c.flag}</span> {c.label}
            </button>
          ))}
        </div>
      )}

      {/* Status sub-subtabs */}
      <div className="flex items-center gap-1 border-b border-zinc-800">
        {statusTabs.map((t) => {
          const active = status === t.id;
          const cls = active
            ? (t.tone === "emerald"
                ? "border-b-2 border-emerald-500 text-emerald-400"
                : "border-b-2 border-rose-500 text-rose-400")
            : "border-b-2 border-transparent text-zinc-400 hover:text-zinc-200";
          return (
            <button key={t.id} onClick={() => setStatus(t.id)} data-testid={t.testId}
              className={`px-4 py-2 text-sm font-medium transition-colors ${cls}`}>
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Bulk actions bar — apare doar când sunt selectii */}
      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-orange-500/40 bg-orange-500/10 px-3 py-2 text-xs" data-testid="incasare-bulk-bar">
          <span className="font-semibold text-orange-200">{selected.size} selectate</span>
          <button
            data-testid="incasare-bulk-mark-paid"
            disabled={bulkBusy}
            onClick={() => runBulkAction("mark_paid")}
            className="inline-flex items-center rounded border border-emerald-500/50 bg-emerald-500/15 px-2.5 py-1 font-medium text-emerald-200 hover:bg-emerald-500/25 disabled:opacity-50">
            ✓ Marchează Încasate
          </button>
          <button
            data-testid="incasare-bulk-mark-unpaid"
            disabled={bulkBusy}
            onClick={() => runBulkAction("mark_unpaid")}
            className="inline-flex items-center rounded border border-zinc-600 bg-zinc-800 px-2.5 py-1 font-medium text-zinc-300 hover:bg-zinc-700 disabled:opacity-50">
            ↺ Marchează Neîncasate
          </button>
          <button
            data-testid="incasare-bulk-clear"
            onClick={clearSelection}
            className="ml-auto text-[11px] text-zinc-400 hover:text-zinc-200 underline">
            Deselectează
          </button>
        </div>
      )}

      {/* Table — same layout pentru ambele canale. Coloanele Comision/Real
          incasat sunt populate numai la Netopia (Gomag/Manual Online).
          eMAG: sursa venitului este desfasuratorul, coloana Comision goala. */}
      {status === "plati_shopify" && channel !== "emag" && <ShopifyPayouts year={year} month={month} onSynced={loadRows} />}
      <div className={`overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950 ${status === "plati_shopify" && channel !== "emag" ? "hidden" : ""}`} data-testid="incasare-table">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900/60 text-xs uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="px-3 py-2 text-left w-8" data-testid="incasare-select-all">
                  <input type="checkbox" checked={rows.length > 0 && selected.size === rows.length} onChange={toggleAll} className="h-3.5 w-3.5 accent-orange-500" />
                </th>
                <th className="px-3 py-2 text-left">Nume</th>
                <th className="px-3 py-2 text-left">Platformă</th>
                <th className="px-3 py-2 text-left">Telefon</th>
                <th className="px-3 py-2 text-left">Nr. comandă</th>
                <th className="px-3 py-2 text-left">Metodă plată</th>
                <th className="px-3 py-2 text-right">Sumă comandă</th>
                {channel === "gomag_manual" && (
                  <>
                    <th className="px-3 py-2 text-right" title="Comision Netopia (Comision + TVA)">Comision</th>
                    <th className="px-3 py-2 text-right" title="Suma reală încasată = Sumă comandă − Comision">Real încasat</th>
                  </>
                )}
                {channel === "emag" && (
                  <th className="px-3 py-2 text-right" title="Sumă − Genius (8) − Fulfillment (5/unit) − Ads% − Comision eMAG%">Real după comision</th>
                )}
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Data</th>
                <th className="px-3 py-2 text-right">Acțiuni</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={channel === "gomag_manual" ? 12 : 11} className="px-3 py-10 text-center text-xs text-zinc-500">Se încarcă...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={channel === "gomag_manual" ? 12 : 11} className="px-3 py-10 text-center text-xs text-zinc-500">Nicio comandă {status === "incasate" ? "încasată" : "neîncasată"} în {MONTHS_RO[month - 1]} {year}.</td></tr>
              ) : rows.map((r) => {
                const k = rowKey(r);
                const isSelected = selected.has(k);
                return (
                <tr key={k} className={`border-t border-zinc-900 hover:bg-zinc-900/40 ${isSelected ? "bg-orange-500/5" : ""}`} data-testid={`incasare-row-${r.order_id}`}>
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      data-testid={`incasare-row-select-${r.order_id}`}
                      checked={isSelected}
                      onChange={() => toggleRow(r)}
                      className="h-3.5 w-3.5 accent-orange-500"
                    />
                  </td>
                  <td className="px-3 py-2 text-zinc-200">{r.nume}</td>
                  <td className="px-3 py-2 text-xs text-zinc-300 whitespace-nowrap">
                    <span className="mr-1">{flagFor(r.integration_key)}</span>
                    {platformLabel(r.integration_key)}
                  </td>
                  <td className="px-3 py-2 text-zinc-400">{r.telefon}</td>
                  <td className="px-3 py-2 font-mono text-xs text-zinc-300">{r.order_id}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex rounded-md px-2 py-0.5 text-[10px] font-medium ${r.metoda_plata === "ramburs" ? "bg-orange-500/15 text-orange-300" : "bg-sky-500/15 text-sky-300"}`}>
                      {r.metoda_plata === "ramburs" ? "Ramburs" : "Online"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right text-zinc-200">{fmt(r.suma, r.currency)}</td>
                  {channel === "gomag_manual" && (
                    <>
                      <td className="px-3 py-2 text-right text-rose-300 text-xs">
                        {r.commission != null ? `−${fmt(r.commission, r.currency)}` : "—"}
                      </td>
                      <td className="px-3 py-2 text-right text-emerald-300 font-medium">
                        {r.real_incasat != null ? fmt(r.real_incasat, r.currency) : (r.metoda_plata === "ramburs" && r.status_incasare === "incasat" ? fmt(r.suma, r.currency) : "—")}
                      </td>
                    </>
                  )}
                  {channel === "emag" && (
                    <td className="px-3 py-2 text-right text-sky-300 font-medium"
                        title={`Genius: ${(r.genius_fee || 0).toFixed(2)}  ·  FF: ${(r.fulfillment_fee || 0).toFixed(2)}  ·  Ads: ${(r.ads_fee || 0).toFixed(2)}  ·  Comision: ${(r.commission_fee || 0).toFixed(2)}  ·  Total deducere: ${(r.deductions_total || 0).toFixed(2)}`}>
                      {fmt(r.real_incasat, r.currency)}
                    </td>
                  )}
                  <td className="px-3 py-2">
                    {r.status_incasare === "incasat" ? (
                      <span className="text-[11px] font-medium text-emerald-400" title={r.paid_note || ""}>
                        ✓ Încasat{r.paid_note ? ` · ${r.paid_note}` : ""}{r.paid_at ? ` · ${String(r.paid_at).slice(0,10)}` : ""}
                      </span>
                    ) : (
                      <span className="text-[11px] font-medium text-rose-400">✗ Neîncasat</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-zinc-500">{r.data || "—"}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {r.status_incasare === "neincasat" ? (
                      <button
                        data-testid={`incasare-mark-paid-${r.order_id}`}
                        onClick={() => runOrderAction(r, "mark_paid")}
                        title="Marchează manual încasat (când vezi în Sameday/Netopia decontarea dar nu s-a matched)"
                        className="mr-1 inline-flex items-center rounded border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-300 hover:bg-emerald-500/20">
                        ✓ Încasat
                      </button>
                    ) : (
                      <button
                        data-testid={`incasare-mark-unpaid-${r.order_id}`}
                        onClick={() => runOrderAction(r, "mark_unpaid")}
                        title="Anulează marcajul de plată"
                        className="mr-1 inline-flex items-center rounded border border-zinc-700 bg-zinc-800/60 px-2 py-0.5 text-[10px] font-medium text-zinc-400 hover:bg-zinc-800">
                        ↺
                      </button>
                    )}
                    {channel === "gomag_manual" && (
                      <button
                        data-testid={`incasare-hide-${r.order_id}`}
                        onClick={() => runOrderAction(r, "hide")}
                        title="Ascunde comanda (ex: stornată/returnată nesincronizată)"
                        className="inline-flex items-center rounded border border-rose-500/40 bg-rose-500/10 px-2 py-0.5 text-[10px] font-medium text-rose-300 hover:bg-rose-500/20">
                        ✕ Ascunde
                      </button>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
    </div>
  );
}

export default function Financiar() {
  const [topTab, setTopTab] = useState("tva"); // tva | incasare | expenses | transport
  const [tab, setTab] = useState("RO"); // RO | BG (only for TVA)

  return (
    <div className="mx-auto max-w-7xl space-y-4 p-4 sm:p-6" data-testid="financiar-page">
      <div className="flex items-center gap-3">
        <Wallet className="text-emerald-400" size={24} />
        <div>
          <h1 className="text-2xl font-bold text-zinc-100">Financiar</h1>
          <p className="text-xs text-zinc-500">TVA · Status Încasare · Cheltuieli &amp; Plăți China · Transport Sameday</p>
        </div>
      </div>

      {/* Top-level tabs */}
      <div className="flex items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-950 p-1">
        <button onClick={() => setTopTab("tva")} data-testid="top-tab-tva"
          className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition-colors inline-flex items-center justify-center gap-1.5 ${
            topTab === "tva" ? "bg-emerald-600 text-white" : "text-zinc-400 hover:text-zinc-100"
          }`}>
          <Receipt size={14} /> TVA
        </button>
        <button onClick={() => setTopTab("incasare")} data-testid="top-tab-incasare"
          className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition-colors inline-flex items-center justify-center gap-1.5 ${
            topTab === "incasare" ? "bg-sky-600 text-white" : "text-zinc-400 hover:text-zinc-100"
          }`}>
          <CreditCard size={14} /> Status Încasare Comenzi
        </button>
        <button onClick={() => setTopTab("expenses")} data-testid="top-tab-expenses"
          className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition-colors inline-flex items-center justify-center gap-1.5 ${
            topTab === "expenses" ? "bg-orange-600 text-white" : "text-zinc-400 hover:text-zinc-100"
          }`}>
          <Wallet size={14} /> Cheltuieli &amp; Plăți China
        </button>
        <button onClick={() => setTopTab("transport")} data-testid="top-tab-transport"
          className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition-colors inline-flex items-center justify-center gap-1.5 ${
            topTab === "transport" ? "bg-cyan-600 text-white" : "text-zinc-400 hover:text-zinc-100"
          }`}>
          <Truck size={14} /> Raport Transport Sameday
        </button>
      </div>

      {topTab === "tva" ? (
        <>
          {/* TVA country tabs (2026-02-04): „TVA DE PLĂTIT BG" redenumit
              „TVA INTRACOMUNITAR" — afiseaza toate facturile non-RON. */}
          <div className="flex items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-950 p-1">
            <button onClick={() => setTab("RO")} data-testid="tab-ro"
              className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                tab === "RO" ? "bg-emerald-600 text-white" : "text-zinc-400 hover:text-zinc-100"
              }`}>
              TVA DE PLĂTIT RO
            </button>
            <button onClick={() => setTab("BG")} data-testid="tab-intracom"
              className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                tab === "BG" ? "bg-emerald-600 text-white" : "text-zinc-400 hover:text-zinc-100"
              }`}>
              TVA INTRACOMUNITAR
            </button>
          </div>
          {tab === "RO" ? <RomaniaView /> : <IntracomunitarView />}
        </>
      ) : topTab === "incasare" ? (
        <StatusIncasareView />
      ) : topTab === "expenses" ? (
        <ExpensesAndChinaView />
      ) : (
        <TransportView />
      )}
    </div>
  );
}
